// ─── Komplexní datové typy a rozsáhlá taxonomie kategorií (poptavky_project) ───

export type UserRole = 'client' | 'craftsman' | 'brigadnik' | 'admin'

export type VerificationStatus = 'pending' | 'verified' | 'rejected'

export type InquiryStatus = 'open' | 'awarded' | 'completed' | 'cancelled'

export type OfferStatus = 'pending' | 'awarded' | 'rejected'

export type TransactionType =
  | 'starter_bonus'
  | 'offer_block'
  | 'offer_unblock'
  | 'offer_deduct'
  | 'credit_purchase'
  | 'admin_adjustment'

/**
 * Určuje, pro koho je daná kategorie práce určena:
 * - 'company_only': Odborná řemesla (elektro, plyn, voda, střechy...) -> vidí POUZE firmy s IČO na webu Firmy-Poptavky
 * - 'brigadnik_only': Výpomoc a brigády -> vidí POUZE brigádníci na webu Brigadnici-Poptavky
 * - 'both': Činnosti vhodné pro firmy i šikovné brigádníky (sekání zahrady, malování, stěhování, úklid...)
 */
export type ProviderTarget = 'company_only' | 'brigadnik_only' | 'both'

export type CategorySuperGroup =
  | 'electrical_group'      // 1. ⚡ Elektro, FVE & Smart Home (8 kategorií)
  | 'plumbing_heating_group'// 2. 💧 Voda, Topení, Plyn & Klima (8 kategorií)
  | 'construction'          // 3. 🧱 Stavba, Zedníci & Fasády (7 kategorií)
  | 'interior_group'        // 4. 🏠 Kuchyně, Koupelny & Interiér (8 kategorií)
  | 'roof_windows_group'    // 5. 🏗️ Střechy, Okna, Kominíci & Kovy (7 kategorií)
  | 'finishing'             // 6. 🎨 Malování, Montáže & Opravy (7 kategorií)
  | 'garden'                // 7. 🌳 Zahrada, Stromy, Ploty & Výkopy (9 kategorií)
  | 'moving_cleaning_group' // 8. 🚚 Stěhování, Vyklízení & Úklid (7 kategorií)
  | 'projects_special_group'// 9. 📐 Projekty, Dozor & Speciální (6 kategorií)

export type TradeCategory =
  // 1. ⚡ Elektro, FVE & Smart Home (8 kategorií)
  | 'electrical'
  | 'elec_switchboard'
  | 'elec_lighting_sockets'
  | 'elec_appliances'
  | 'elec_revisions_lps'
  | 'elec_solar_wallbox'
  | 'elec_smart_security'
  | 'elec_emergency'
  // 2. 💧 Voda, Topení, Plyn & Klima (8 kategorií)
  | 'plumbing'
  | 'plumbing_sanitary'
  | 'plumbing_boilers'
  | 'plumbing_drains'
  | 'heating'
  | 'heating_boilers_pumps'
  | 'gas_hvac'
  | 'aircon_ventilation'
  // 3. 🧱 Stavba, Zedníci & Fasády (7 kategorií)
  | 'rough_construction'
  | 'masonry'
  | 'drywall_attic'
  | 'concrete_leveling'
  | 'facades_insulation'
  | 'waterproofing_damp'
  | 'demolition_core_drilling'
  // 4. 🏠 Kuchyně, Koupelny & Interiér (8 kategorií)
  | 'kitchen'
  | 'kitchen_assembly'
  | 'bathroom'
  | 'tiling'
  | 'flooring'
  | 'parquet_renovation'
  | 'carpentry'
  | 'reconstruction'
  // 5. 🏗️ Střechy, Okna, Kominíci & Kovy (7 kategorií)
  | 'roofing'
  | 'carpentry_roof_pergola'
  | 'tinsmith_gutters'
  | 'chimney_sweeps'
  | 'windows_doors'
  | 'blinds_shading'
  | 'locksmith_metalwork'
  // 6. 🎨 Malování, Montáže & Opravy (7 kategorií)
  | 'painting'
  | 'coating_lacquering'
  | 'wallpapering_stucco'
  | 'furniture_assembly'
  | 'drilling_hanging'
  | 'handyman'
  | 'locks_security_doors'
  // 7. 🌳 Zahrada, Stromy, Ploty & Výkopy (9 kategorií)
  | 'garden_mowing'
  | 'garden_hedges_weeding'
  | 'garden_planting_design'
  | 'wood_chopping'
  | 'tree_felling_expert'
  | 'stump_grinding_chipping'
  | 'fencing_gates'
  | 'fencing_paving'
  | 'excavation_wells'
  // 8. 🚚 Stěhování, Vyklízení & Úklid (7 kategorií)
  | 'moving_apartments'
  | 'moving_help'
  | 'clearing_cellars'
  | 'demolition_digging'
  | 'home_cleaning'
  | 'window_upholstery_cleaning'
  | 'post_renovation_cleaning'
  // 9. 📐 Projekty, Dozor & Speciální (6 kategorií)
  | 'architect_interior'
  | 'project_engineer'
  | 'building_supervisor'
  | 'geodesist'
  | 'pest_control'
  | 'other'

export interface SuperGroupInfo {
  id: CategorySuperGroup
  tabLabel: string // Jasný neskracovaný název pro záložku (žádné .split(',')[0]!)
  title: string
  subtitle: string
  badge: string
}

export const CATEGORY_SUPER_GROUPS: SuperGroupInfo[] = [
  {
    id: 'electrical_group',
    tabLabel: '⚡ Elektro, FVE & Smart Home',
    title: 'Elektroinstalace, fotovoltaika a chytrá domácnost',
    subtitle: 'Elektrikáři, rozvaděče, revize, FVE, wallboxy a zabezpečení',
    badge: 'Odborné firmy a elektrikáři',
  },
  {
    id: 'plumbing_heating_group',
    tabLabel: '💧 Voda, Topení, Plyn & Klima',
    title: 'Voda, kanalizace, topení, plyn a klimatizace',
    subtitle: 'Instalatéři, tepelná čerpadla, kotle, odpady a rekuperace',
    badge: 'Odborné firmy s oprávněním',
  },
  {
    id: 'construction',
    tabLabel: '🧱 Stavba, Zedníci & Fasády',
    title: 'Hrubá stavba, zednické práce, sádrokartony a fasády',
    subtitle: 'Základové desky, omítky, zateplení, potěry, sanace a bourání',
    badge: 'Stavební a zednické firmy',
  },
  {
    id: 'interior_group',
    tabLabel: '🏠 Kuchyně, Koupelny & Interiér',
    title: 'Kuchyně, rekonstrukce koupelen, podlahy a truhlářství',
    subtitle: 'Kuchyně na míru, obklady, bytová jádra, vinyly, parkety a rekonstrukce',
    badge: 'Odborné firmy a řemeslníci',
  },
  {
    id: 'roof_windows_group',
    tabLabel: '🏗️ Střechy, Okna, Kominíci & Kovy',
    title: 'Střechy, klempíři, okna, dveře, kominictví a kovovýroba',
    subtitle: 'Pokrývači, tesaři, okna, žaluzie, komíny a zámečnictví',
    badge: 'Specializované firmy a řemeslníci',
  },
  {
    id: 'finishing',
    tabLabel: '🎨 Malování, Montáže & Opravy',
    title: 'Malování, tapetování, montáž nábytku a údržba',
    subtitle: 'Malíři pokojů, nátěry, montáž IKEA, věšení polic a hodinový manžel',
    badge: 'Firmy i šikovní pomocníci',
  },
  {
    id: 'garden',
    tabLabel: '🌳 Zahrada, Stromy, Ploty & Výkopy',
    title: 'Údržba zahrady, rizikové kácení, ploty a dlažby',
    subtitle: 'Sekání trávy, živé ploty, štípání dřeva, kácení, ploty a výkopy',
    badge: 'Zahradníci, firmy i brigádníci',
  },
  {
    id: 'moving_cleaning_group',
    tabLabel: '🚚 Stěhování, Vyklízení & Úklid',
    title: 'Stěhování, vyklízení, odvoz a kompletní úklid',
    subtitle: 'Stěhování bytů, výpomoc s nošením, vyklízení půd a generální úklid',
    badge: 'Stěhovací firmy i brigádníci',
  },
  {
    id: 'projects_special_group',
    tabLabel: '📐 Projekty, Dozor & Speciální',
    title: 'Architekti, projektanti, stavební dozor a speciální služby',
    subtitle: '3D vizualizace, projekty pro povolení, geodézie a deratizace',
    badge: 'Certifikovaní odborníci',
  },
]

export interface TradeCategoryInfo {
  id: TradeCategory
  group: CategorySuperGroup
  label: string
  shortDesc: string
  providerTarget: ProviderTarget
  creditCost: number // Kolik kreditů se rezervuje při poslání nabídky
  supportsFloorplan: boolean // Zda dává smysl nabízet nákres místnosti
  subTasks: string[] // Konkrétní podčinnosti daného řemesla (uživatel si zakliká přímo své práce)
  placeholderTitle: string
  placeholderDesc: string
  scaleQuestions: {
    unitLabel: string
    presets: string[]
  }
}

export const TRADE_CATEGORIES: TradeCategoryInfo[] = [
  // ─── 1. ⚡ ELEKTRO, FOTOVOLTAIKA & SMART HOME (8 KATEGORIÍ) ──────────────────
  {
    id: 'electrical',
    group: 'electrical_group',
    label: 'Bytová a domovní elektroinstalace',
    shortDesc: 'Kompletní nové rozvody v bytě či domě, výměna hliníku za měď, drážkování a elektro pro kuchyň',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Kompletní nové rozvody v bytě nebo domě',
      'Výměna starých hliníkových rozvodů za měď',
      'Drážkování a tahání kabelových tras',
      'Příprava elektroinstalace pro novou kuchyň',
      'Posun zásuvek a vypínačů dle dispozice',
    ],
    placeholderTitle: 'Např. Kompletní nové rozvody elektřiny v panelovém bytě 3+1',
    placeholderDesc:
      'Uveďte zda jde o cihlu nebo panel, rozsah místností a zda potřebujete i zasekání kabelů...',
    scaleQuestions: {
      unitLabel: 'Rozsah elektroinstalace',
      presets: [
        'Jedna místnost (např. kuchyň)',
        'Celý byt (do 75 m²)',
        'Celý rodinný dům',
        'Novostavba na klíč',
      ],
    },
  },
  {
    id: 'elec_switchboard',
    group: 'electrical_group',
    label: 'Rozvaděče, jističe a el. přípojky',
    shortDesc: 'Výměna domovního rozvaděče, nové jističe, proudové chrániče, elektroměrové pilíře a navýšení jističe',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Výměna staré pojistkové skříně za moderní rozvaděč',
      'Instalace proudových chráničů a přepěťových ochran',
      'Úprava elektroměrového rozvaděče (pro ČEZ/E.ON/PRE)',
      'Navýšení hlavního jističe nebo změna sazby',
      'Nová venkovní přípojka k rodinnému domu',
    ],
    placeholderTitle: 'Např. Výměna hlavního domovního rozvaděče a doplnění proudových chráničů',
    placeholderDesc:
      'Popište stav stávajícího rozvaděče, požadavek na počet jističů a zda potřebujete potvrzení pro distributora...',
    scaleQuestions: {
      unitLabel: 'Typ prací na rozvaděči',
      presets: [
        'Výměna bytového rozvaděče',
        'Úprava elektroměrového pilíře',
        'Nová přípojka pro rodinný dům',
        'Rozšíření jističů pro nový spotřebič',
      ],
    },
  },
  {
    id: 'elec_lighting_sockets',
    group: 'electrical_group',
    label: 'Zásuvky, vypínače a osvětlení',
    shortDesc: 'Montáž a posun zásuvek a vypínačů, zavěšení lustrů, LED pásky, stropní svítidla a venkovní osvětlení',
    providerTarget: 'company_only',
    creditCost: 10,
    supportsFloorplan: true,
    subTasks: [
      'Montáž nebo výměna zásuvek a vypínačů',
      'Posun zásuvek do nové polohy',
      'Zavěšení a zapojení lustrů a stropních světel',
      'Montáž podhledových bodových světel a LED pásků',
      'Instalace venkovního osvětlení a pohybových čidel',
    ],
    placeholderTitle: 'Např. Výměna 12 zásuvek a zavěšení tří lustrů v bytě',
    placeholderDesc:
      'Uveďte počet svítidel či zásuvek k montáži a zda je materiál již nakoupen...',
    scaleQuestions: {
      unitLabel: 'Počet svítidel a zásuvek',
      presets: [
        'Do 5 kusů',
        '6 až 15 kusů',
        '16 a více kusů (celý byt/dům)',
        'Venkovní osvětlení zahrady / fasády',
      ],
    },
  },
  {
    id: 'elec_appliances',
    group: 'electrical_group',
    label: 'Zapojení varné desky a spotřebičů',
    shortDesc: 'Odborné zapojení indukční a varné desky (400V/230V), trouby, digestoře a bojleru s potvrzením záruky',
    providerTarget: 'company_only',
    creditCost: 10,
    supportsFloorplan: false,
    subTasks: [
      'Odborné zapojení indukční varné desky (230V / 400V)',
      'Zapojení vestavné trouby a varného setu',
      'Potvrzení záručního listu spotřebiče elektrikářem',
      'Připojení digestoře a odtahu par',
      'Zapojení elektrického bojleru',
    ],
    placeholderTitle: 'Např. Odborné zapojení indukční desky a trouby s razítkem do záručního listu',
    placeholderDesc:
      'Uveďte typ varné desky, zda je na místě připraven třífázový kabel (400V) nebo 230V...',
    scaleQuestions: {
      unitLabel: 'Počet a typ spotřebičů k zapojení',
      presets: [
        '1 spotřebič (např. indukční deska)',
        'Varná deska + vestavná trouba',
        'Kompletní set spotřebičů do kuchyně',
        'Elektrický bojler nebo topidlo',
      ],
    },
  },
  {
    id: 'elec_revisions_lps',
    group: 'electrical_group',
    label: 'Revize elektro a hromosvody',
    shortDesc: 'Výchozí revize pro kolaudaci, pravidelné revize elektroinstalace, montáž a revize hromosvodů (LPS)',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Výchozí revize elektroinstalace pro kolaudaci',
      'Pravidelná revize elektroinstalace (byty, domy, firmy)',
      'Revize odběrného místa pro distributora energie',
      'Montáž nového hromosvodu na dům',
      'Pravidelná revize a oprava hromosvodu (LPS)',
    ],
    placeholderTitle: 'Např. Výchozí revizní zpráva elektro pro kolaudaci novostavby RD',
    placeholderDesc:
      'Napište o jaký typ objektu se jedná, zda máte projektovou dokumentaci a pro jaký účel revizi potřebujete...',
    scaleQuestions: {
      unitLabel: 'Typ požadované revize',
      presets: [
        'Revize bytu (kolaudace / po rekonstrukci)',
        'Revize rodinného domu',
        'Revize hromosvodu',
        'Komerční prostory / bytový dům',
      ],
    },
  },
  {
    id: 'elec_solar_wallbox',
    group: 'electrical_group',
    label: 'Fotovoltaika (FVE) a Wallboxy',
    shortDesc: 'Střešní fotovoltaická elektrárna, bateriová úložiště, nabíjecí stanice pro elektromobily a ohřev vody',
    providerTarget: 'company_only',
    creditCost: 25,
    supportsFloorplan: false,
    subTasks: [
      'Návrh a instalace střešní fotovoltaické elektrárny (FVE)',
      'Bateriové úložiště a hybridní střídače',
      'Instalace domácí nabíjecí stanice pro elektromobil (Wallbox)',
      'Fotovoltaický ohřev teplé vody',
      'Připojení do distribuční sítě a vyřízení dotace',
    ],
    placeholderTitle: 'Např. Instalace domácí FVE 6 kWp s bateriovým úložištěm a Wallboxem',
    placeholderDesc:
      'Popište typ střechy, roční spotřebu elektřiny a zda máte zájem o řešení na klíč včetně dotace...',
    scaleQuestions: {
      unitLabel: 'Požadovaný rozsah instalace',
      presets: [
        'Samostatný Wallbox pro elektromobil',
        'Malá FVE bez baterie / ohřev vody',
        'Kompletní rodinná FVE s baterií (5–10 kWp)',
        'Firemní nebo velká instalace (>10 kWp)',
      ],
    },
  },
  {
    id: 'elec_smart_security',
    group: 'electrical_group',
    label: 'Kamery, alarmy, datové sítě a Smart Home',
    shortDesc: 'Kamerové systémy (CCTV), alarmy (Jablotron/EZS), domácí videotelefony, LAN/Wi-Fi rozvody a chytrá domácnost',
    providerTarget: 'company_only',
    creditCost: 18,
    supportsFloorplan: true,
    subTasks: [
      'Instalace kamerového systému se záznamem (CCTV)',
      'Zabezpečovací systém a alarm (Jablotron, Paradox apod.)',
      'Domácí videotelefon a elektronický vrátný',
      'Datové rozvody LAN, patch panely a Wi-Fi pokrytí',
      'Chytrá domácnost (Loxone, Philips Hue, chytré ovládání topení)',
    ],
    placeholderTitle: 'Např. Zabezpečovací systém Jablotron a 4 venkovní kamery pro rodinný dům',
    placeholderDesc:
      'Uveďte počet kamer nebo čidel a zda jsou v domě připraveny kabely, nebo preferujete bezdrátové řešení...',
    scaleQuestions: {
      unitLabel: 'Rozsah zabezpečení a sítí',
      presets: [
        'Zabezpečení bytu (alarm + vstup)',
        'Rodinný dům (kamery + alarm)',
        'Datové rozvody LAN a silná Wi-Fi',
        'Kompletní systém chytré domácnosti',
      ],
    },
  },
  {
    id: 'elec_emergency',
    group: 'electrical_group',
    label: 'Opravy poruch elektro a hledání zkratu',
    shortDesc: 'Vyhazující jističe, nefunkční zásuvkové okruhy, spálené zásuvky a lokalizace přerušeného kabelu ve zdi',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Oprava vyhazujícího jističe nebo proudového chrániče',
      'Hledání a oprava zkratu v elektrickém okruhu',
      'Oprava vyhořelé zásuvky nebo vypínače',
      'Detekce a oprava přerušeného kabelu ve zdi',
      'Havarijní oprava výpadku proudu v části objektu',
    ],
    placeholderTitle: 'Např. Hledání zkratu – hlavní chránič neustále padá při zapnutí kuchyně',
    placeholderDesc:
      'Popište přesně co poruše předcházelo (např. vrtání do zdi, zapnutí spotřebiče) a jak se projevuje...',
    scaleQuestions: {
      unitLabel: 'Naléhavost a typ poruchy',
      presets: [
        'Nefunkční jednotlivá zásuvka / světlo',
        'Výpadek celého okruhu / vyhazující jistič',
        'Převrtaný kabel ve zdi',
        'Kompletní výpadek proudu v nemovitosti',
      ],
    },
  },

  // ─── 2. 💧 VODA, TOPENÍ, PLYN & KLIMATIZACE (8 KATEGORIÍ) ───────────────────
  {
    id: 'plumbing',
    group: 'plumbing_heating_group',
    label: 'Vodoinstalatér a rozvody vody',
    shortDesc: 'Kompletní rozvody vody a odpadů, stoupačky, přípojky pro kuchyň a myčku a venkovní vodovod',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Kompletní rozvody vody a odpadů (PPR / PEX)',
      'Přípojky vody a odpadu pro kuchyň a myčku',
      'Výměna ležatých a svislých stoupaček',
      'Venkovní vodovodní přípojka a nezámrzný kohout',
      'Havarijní oprava prasklé trubky ve zdi',
    ],
    placeholderTitle: 'Např. Kompletní nové rozvody vody a odpadu pro koupelnu a kuchyň',
    placeholderDesc:
      'Uveďte rozsah prostor, zda jde o novostavbu nebo rekonstrukci a zda potřebujete i zasekání trubek...',
    scaleQuestions: {
      unitLabel: 'Rozsah instalatérských prací',
      presets: [
        'Přípojky pro kuchyň a myčku',
        'Kompletní koupelna a WC',
        'Celý byt / rodinný dům',
        'Havarijní oprava úniku vody',
      ],
    },
  },
  {
    id: 'plumbing_sanitary',
    group: 'plumbing_heating_group',
    label: 'Výměna baterií, WC, umyvadel a dřezů',
    shortDesc: 'Montáž a výměna vodovodních baterií, umyvadel, dřezů, sprchových koutů, Geberitů a výměna sifonů',
    providerTarget: 'company_only',
    creditCost: 10,
    supportsFloorplan: false,
    subTasks: [
      'Výměna dřezové nebo umyvadlové baterie',
      'Montáž a výměna závěsného WC modulu (Geberit)',
      'Výměna nebo montáž umyvadla, dřezu a sifonu',
      'Instalace sprchové vaničky a koutu / zástěny',
      'Montáž a silikonování vany',
    ],
    placeholderTitle: 'Např. Výměna dvou pákových baterií a montáž nového umyvadla se skříňkou',
    placeholderDesc:
      'Uveďte o jakou sanitu se jedná a zda již máte nakoupený nový materiál...',
    scaleQuestions: {
      unitLabel: 'Počet zařizovacích předmětů',
      presets: [
        '1 kus (např. baterie nebo WC)',
        '2 až 3 kusy sanity',
        'Kompletní vybavení koupelny a WC',
      ],
    },
  },
  {
    id: 'plumbing_boilers',
    group: 'plumbing_heating_group',
    label: 'Bojlery, ohřívače vody a domácí vodárny',
    shortDesc: 'Montáž a výměna elektrických i kombinovaných bojlerů, čištění od vodního kamene a čerpadla do studní',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Výměna a zapojení elektrického bojleru',
      'Montáž kombinovaného nebo akumulačního ohřívače vody',
      'Čištění bojleru a odvápnění topného tělesa',
      'Instalace domácí vodárny nebo ponorného čerpadla',
      'Montáž tlakové nádoby a filtrace vody',
    ],
    placeholderTitle: 'Např. Výměna starého kapajícího bojleru 120 l za nový elektrický bojler',
    placeholderDesc:
      'Uveďte objem stávajícího bojleru, zda je zavěšený na zdi nebo stojatý a zda požadujete i ekologickou likvidaci...',
    scaleQuestions: {
      unitLabel: 'Objem a typ bojleru / čerpadla',
      presets: [
        'Bojler do 80 litrů',
        'Bojler 100 až 160 litrů',
        'Velký zásobník 200+ litrů',
        'Čerpadlo do studny / domácí vodárna',
      ],
    },
  },
  {
    id: 'plumbing_drains',
    group: 'plumbing_heating_group',
    label: 'Čištění ucpaných odpadů a kanalizace',
    shortDesc: 'Strojní čištění ucpaných dřezů, WC a stoupaček, tlakové čištění kanalizace a kamerová revize potrubí',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Strojní čištění ucpaného dřezu, vany nebo sprchy perem',
      'Čištění ucpané toalety (WC)',
      'Frézování a strojní čištění svislých stoupaček',
      'Tlakové čištění ležaté domovní kanalizace vozem',
      'Kamerová inspekce odpadního potrubí se záznamem',
    ],
    placeholderTitle: 'Např. Strojní pročištění ucpaného kuchyňského odpadu a revize kamerou',
    placeholderDesc:
      'Popište co přesně neodtéká, jak dlouho problém trvá a zda se jedná o byt nebo rodinný dům...',
    scaleQuestions: {
      unitLabel: 'Kde problém s odpadem nastal',
      presets: [
        'Domácí odpad (dřez, umyvadlo, WC, sprcha)',
        'Stoupačka v bytovém domě',
        'Venkovní svod / ležatá kanalizace',
        'Kamerová revize stavu potrubí',
      ],
    },
  },
  {
    id: 'heating',
    group: 'plumbing_heating_group',
    label: 'Podlahové topení a výměna radiátorů',
    shortDesc: 'Teplovodní podlahové topení, elektrické topné rohože, posun a výměna radiátorů a termostatické ventily',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: true,
    subTasks: [
      'Montáž teplovodního podlahového vytápění na systémovou desku',
      'Pokládka elektrických topných rohoží nebo fólií',
      'Výměna starých litinových radiátorů za deskové',
      'Doplnění termostatických ventilů a hlavic',
      'Napuštění, odvzdušnění a vyvážení otopného systému',
    ],
    placeholderTitle: 'Např. Pokládka teplovodního podlahového topení v přízemí RD (70 m²)',
    placeholderDesc:
      'Uveďte podlahovou plochu pro podlahové topení nebo počet radiátorů k výměně...',
    scaleQuestions: {
      unitLabel: 'Rozsah topenářských prací',
      presets: [
        'Výměna 1 až 3 radiátorů',
        'Podlahové topení v jedné místnosti / koupelně',
        'Celý byt (do 80 m²)',
        'Celý rodinný dům (nad 80 m²)',
      ],
    },
  },
  {
    id: 'heating_boilers_pumps',
    group: 'plumbing_heating_group',
    label: 'Tepelná čerpadla a kotle (tuhá paliva, elektro)',
    shortDesc: 'Montáž tepelných čerpadel vzduch-voda, instalace elektrokotlů, kotlů na pelety/dřevo a akumulačních nádrží',
    providerTarget: 'company_only',
    creditCost: 25,
    supportsFloorplan: false,
    subTasks: [
      'Instalace tepelného čerpadla vzduch-voda',
      'Montáž elektrokotle včetně regulace',
      'Instalace automatického kotle na pelety nebo dřevo',
      'Montáž akumulační nádrže a bojleru TZB',
      'Výměna starého kotle v kotelně na klíč',
    ],
    placeholderTitle: 'Např. Výměna starého uhelného kotle za tepelné čerpadlo vzduch-voda',
    placeholderDesc:
      'Uveďte tepelnou ztrátu domu (pokud ji znáte), vytápěnou plochu a zda žádáte o Kotlíkovou dotaci / NZÚ...',
    scaleQuestions: {
      unitLabel: 'Druh nového zdroje tepla',
      presets: [
        'Tepelné čerpadlo vzduch-voda',
        'Elektrokotel s regulací',
        'Kotel na pelety / dřevoplyn',
        'Výměna a zapojení akumulační nádrže',
      ],
    },
  },
  {
    id: 'gas_hvac',
    group: 'plumbing_heating_group',
    label: 'Plynaři, plynové kotle a revize plynu',
    shortDesc: 'Výměna a servis kondenzačních plynových kotlů, připojení plynových sporáků, rozvody a revize plynu',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: false,
    subTasks: [
      'Výměna a zapojení plynového kondenzačního kotle',
      'Odborné zapojení plynového sporáku nebo varné desky',
      'Rozvody plynu v mědi a nerezových vlnovcích',
      'Výchozí nebo pravidelná revize plynového zařízení',
      'Oprava úniku plynu a servis hořáků',
    ],
    placeholderTitle: 'Např. Výměna plynového kotle za nový kondenzační kotel a revize plynu',
    placeholderDesc:
      'Napište jaký typ kotle nyní máte, zda je zajištěn odtah spalin a zda potřebujete i novou revizní zprávu...',
    scaleQuestions: {
      unitLabel: 'Typ plynařské práce',
      presets: [
        'Připojení plynového sporáku / desky',
        'Výměna plynového kotle',
        'Nové rozvody plynu v objektu',
        'Pravidelná revize plynu a spalin',
      ],
    },
  },
  {
    id: 'aircon_ventilation',
    group: 'plumbing_heating_group',
    label: 'Klimatizace, rekuperace a vzduchotechnika',
    shortDesc: 'Montáž klimatizací do bytů i domů (Split/Multisplit), servis klimatizací, řízené větrání a odtah digestoří',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: true,
    subTasks: [
      'Montáž klimatizace Split (1 vnitřní + 1 venkovní jednotka)',
      'Montáž klimatizace Multisplit (2 až 4 vnitřní jednotky)',
      'Pravidelný servis, dezinfekce a doplnění chladiva',
      'Instalace centrální nebo lokální rekuperace',
      'Montáž vzduchotechnického potrubí a odtahu digestoře',
    ],
    placeholderTitle: 'Např. Montáž klimatizace se dvěma vnitřními jednotkami do podkrovního bytu',
    placeholderDesc:
      'Uveďte počet místností, kam se umístí venkovní jednotka (balkon, střecha, fasáda) a zda je k dispozici přívod elektřiny...',
    scaleQuestions: {
      unitLabel: 'Počet klimatizovaných místností',
      presets: [
        '1 místnost (Single Split)',
        '2 až 3 místnosti (Multisplit)',
        'Celý rodinný dům / centrální rekuperace',
        'Čištění a pravidelný servis klimatizace',
      ],
    },
  },

  // ─── 3. 🧱 STAVBA, ZEDNÍCI, FASÁDY & BOURÁNÍ (7 KATEGORIÍ) ─────────────────
  {
    id: 'rough_construction',
    group: 'construction',
    label: 'Hrubá stavba, základové desky a přístavby',
    shortDesc: 'Základové desky na klíč, zdění nosných zdí (Porotherm/Ytong), stropy, věnce a přístavby garáží/domů',
    providerTarget: 'company_only',
    creditCost: 30,
    supportsFloorplan: false,
    subTasks: [
      'Základová deska pro rodinný dům na klíč',
      'Zdění obvodových a nosných zdí (Ytong, Porotherm, VPC)',
      'Montáž stropních konstrukcí (Miako, panely) a železobetonový věnec',
      'Přístavba rodinného domu, garáže nebo verandy',
      'Hrubá stavba novostavby na klíč',
    ],
    placeholderTitle: 'Např. Realizace základové desky pro bungalov 130 m²',
    placeholderDesc:
      'Uveďte zastavěnou plochu, sklon pozemku a zda máte hotový prováděcí projekt a stavební povolení...',
    scaleQuestions: {
      unitLabel: 'Zastavěná plocha objektu',
      presets: [
        'Přístavba nebo garáž (do 50 m²)',
        'Menší dům (50 až 100 m²)',
        'Běžný rodinný dům (100 až 180 m²)',
        'Velký objekt nad 180 m²',
      ],
    },
  },
  {
    id: 'masonry',
    group: 'construction',
    label: 'Zednické práce, příčky, omítky a štuky',
    shortDesc: 'Zdění vnitřních příček, strojní i ruční omítky, štukování, perlinka s lepidlem a zapravení špalet',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Zdění vnitřních příček (Ytong, sádrové tvárnice, cihly)',
      'Strojní omítky (sádrové nebo vápenocementové)',
      'Ruční štukování a natahování lepidla s perlinkou',
      'Zednické zapravení špalet po výměně oken a dveří',
      'Drobné opravy poškozeného zdiva a prasklin',
    ],
    placeholderTitle: 'Např. Vyzdění příček z Ytongu a natažení perlinky se štukem (45 m²)',
    placeholderDesc:
      'Uveďte přibližnou plochu stěn v m², zda jde o novostavbu či starší dům a zda máte materiál...',
    scaleQuestions: {
      unitLabel: 'Plocha zednických prací',
      presets: [
        'Drobné opravy a špalety (do 15 m²)',
        'Jedna místnost (15 až 40 m²)',
        'Celý byt / patro (40 až 120 m²)',
        'Velká stavba nad 120 m²',
      ],
    },
  },
  {
    id: 'drywall_attic',
    group: 'construction',
    label: 'Sádrokartony (SDK), podhledy a podkroví',
    shortDesc: 'Sádrokartonové stropy a podhledy, SDK příčky, zateplení podkroví minerální vatou s parozábranou a kastlíky',
    providerTarget: 'company_only',
    creditCost: 18,
    supportsFloorplan: true,
    subTasks: [
      'Sádrokartonové stropy a snížené podhledy s LED rampami',
      'Sádrokartonové akustické a dělicí příčky',
      'Zateplení podkroví vatou, montáž parozábrany a SDK záklopu',
      'Kastlíky pro zakrytí trubek, stoupaček a vzduchotechniky',
      'Tmelení a broušení SDK do kvality Q2 až Q4',
    ],
    placeholderTitle: 'Např. Zateplení podkroví a montáž SDK stropů v novostavbě (85 m²)',
    placeholderDesc:
      'Uveďte plochu SDK konstrukcí v m², tloušťku požadované izolace a požadovanou kvalitu povrchu...',
    scaleQuestions: {
      unitLabel: 'Plocha montáže sádrokartonu',
      presets: [
        'Jedna místnost / kastlíky (do 25 m²)',
        'Bytové podhledy (25 až 60 m²)',
        'Kompletní podkroví (60 až 140 m²)',
        'Velký komerční prostor',
      ],
    },
  },
  {
    id: 'concrete_leveling',
    group: 'construction',
    label: 'Betonování, lité podlahy a samonivelace',
    shortDesc: 'Betonové a anhydritové lité potěry, samonivelační stěrky na nerovné podlahy a betony v garážích',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Lité anhydritové a cementové potěry pro podlahové topení',
      'Vyrovnání podkladu samonivelační stěrkou pod podlahy',
      'Betonování podlahy v garáži, dílně nebo sklepě',
      'Spádové klíny a potěry na balkonech a terasách',
      'Frézování a broušení starého betonu',
    ],
    placeholderTitle: 'Např. Vylití samonivelační stěrky v bytě 65 m² před pokládkou vinylu',
    placeholderDesc:
      'Uveďte plochu v m², orientační tloušťku vrstvy v milimetrech a současný stav podkladu...',
    scaleQuestions: {
      unitLabel: 'Plocha podlah k vylití',
      presets: [
        'Malá plocha (do 20 m²)',
        'Střední plocha (20 až 60 m²)',
        'Celý dům / patro (60 až 150 m²)',
        'Průmyslová podlaha nad 150 m²',
      ],
    },
  },
  {
    id: 'facades_insulation',
    group: 'construction',
    label: 'Zateplení fasády, fasádní omítky a lešení',
    shortDesc: 'Zateplení domu polystyrenem či minerální vatou, natažení silikonové omítky, sokly (marmolit) a nátěry fasád',
    providerTarget: 'company_only',
    creditCost: 25,
    supportsFloorplan: false,
    subTasks: [
      'Zateplení fasády polystyrenem (EPS) nebo minerální vatou',
      'Natažení finální probarvené silikonové omítky',
      'Zateplení a obklad soklu soklovou deskou a marmolitem',
      'Mytí, penetrace a ochranný renovační nátěr staré fasády',
      'Zajištění a stavba fasádního lešení',
    ],
    placeholderTitle: 'Např. Kompletní zateplení fasády rodinného domu polystyrenem 16 cm (180 m²)',
    placeholderDesc:
      'Uveďte orientační plochu fasády v m², výšku stavby a zda je potřeba zajistit lešení a vyřídit NZÚ...',
    scaleQuestions: {
      unitLabel: 'Plocha zateplení fasády',
      presets: [
        'Lokální oprava / nátěr (do 50 m²)',
        'Menší dům (50 až 120 m²)',
        'Běžný rodinný dům (120 až 250 m²)',
        'Velký bytový dům nad 250 m²',
      ],
    },
  },
  {
    id: 'waterproofing_damp',
    group: 'construction',
    label: 'Hydroizolace, sanace vlhkosti a odizolování',
    shortDesc: 'Podřezání a chemická injektáž vlhkého zdiva, nopové fólie kolem základů, sanační omítky a izolace teras',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: false,
    subTasks: [
      'Dodatečná chemická injektáž zdiva proti vzlínající vlhkosti',
      'Mechanické podřezání cihlového nebo kamenného zdiva',
      'Odvodnění a drenáž základů vč. montáže nopové fólie',
      'Aplikace sanačních omítek ve sklepních prostorech',
      'Hydroizolace venkovní terasy, balkonu nebo ploché střechy',
    ],
    placeholderTitle: 'Např. Sanace vlhkého soklu a injektáž obvodového zdiva staršího domu',
    placeholderDesc:
      'Popište z jakého materiálu jsou zdi postaveny, tloušťku zdiva a jak vysoko vlhkost sahá...',
    scaleQuestions: {
      unitLabel: 'Rozsah sanačních prací',
      presets: [
        'Jedna vlhká zeď / místnost',
        'Suterén nebo sklepní prostory',
        'Kompletní obvod domu (drenáž + injektáž)',
        'Hydroizolace terasy nebo střechy',
      ],
    },
  },
  {
    id: 'demolition_core_drilling',
    group: 'construction',
    label: 'Bourací práce, vyřezávání otvorů a jádrové vrtání',
    shortDesc: 'Vybourání příček a bytových jader, vyřezání otvorů pro dveře do panelu, osekání obkladů a jádrové vrtání',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Vybourání starého umakartového nebo zděného jádra v bytě',
      'Vyřezání otvoru pro dveře do panelové nebo cihlové stěny',
      'Osekání starých keramických obkladů a dlažby',
      'Jádrové vrtání do betonu pro rekuperaci, digestoř nebo trubky',
      'Naložení suti a zajištění odvozu kontejnerem',
    ],
    placeholderTitle: 'Např. Vybourání nenosné příčky v bytě a odvoz suti do kontejneru',
    placeholderDesc:
      'Uveďte zda se jedná o panel nebo cihlu, v jakém patře se byt nachází a zda je k dispozici výtah...',
    scaleQuestions: {
      unitLabel: 'Rozsah bouracích prací',
      presets: [
        'Osekání obkladů a drobný prostup',
        'Vybourání jedné příčky / jádra',
        'Kompletní vybourání celého bytu na cihlu',
        'Jádrové vrtání prostupů pro digestoř/klimatizaci',
      ],
    },
  },

  // ─── 4. 🏠 KUCHYNĚ, KOUPELNY, PODLAHY & TRUHLÁŘI (8 KATEGORIÍ) ─────────────
  {
    id: 'kitchen',
    group: 'interior_group',
    label: 'Kuchyně na míru (výroba a návrh)',
    shortDesc: 'Zaměření a výroba kuchyně na míru truhlářem, kuchyňské ostrůvky, prémiové materiály a pracovní desky',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: true,
    subTasks: [
      'Zaměření a 3D návrh kuchyně na míru',
      'Výroba kuchyňských skříněk z lamina, laku nebo masivu',
      'Dodávka pracovní desky (kompakt, technický kámen, masiv)',
      'Výroba a montáž kuchyňského ostrůvku a barového pultu',
      'Kompletní montáž linky včetně LED osvětlení',
    ],
    placeholderTitle: 'Např. Výroba kuchyně na míru do tvaru L s ostrůvkem v novostavbě',
    placeholderDesc:
      'Uveďte rozměry prostoru, představu o barvách a materiálu pracovní desky a zda máte nákres...',
    scaleQuestions: {
      unitLabel: 'Tvar a velikost kuchyňské linky',
      presets: [
        'Rovná kuchyňská linka (do 3 metrů)',
        'Rohová kuchyň do L (3 až 6 metrů)',
        'Velká kuchyň do U nebo s ostrůvkem',
        'Kuchyň s prémiovou kamennou deskou',
      ],
    },
  },
  {
    id: 'kitchen_assembly',
    group: 'interior_group',
    label: 'Montáž zakoupené kuchyně (IKEA, Oresi apod.)',
    shortDesc: 'Sestavení a pověšení skříněk koupené kuchyně, přesné výřezy do pracovní desky a usazení dřezu',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Sestavení korpusů a zavěšení horních skříněk na lištu',
      'Přesný výřez a osazení pracovní desky (pro dřez a varnou desku)',
      'Montáž krycích panelů, soklů, těsnicích lišt a úchytek',
      'Usazení a zapojení dřezu, sifonu a baterie',
      'Zabudování a seřízení vestavných spotřebičů',
    ],
    placeholderTitle: 'Např. Odborná montáž kuchyně IKEA Metod vč. výřezu pro dřez a desku',
    placeholderDesc:
      'Uveďte výrobce a rozměry kuchyně, zda je materiál již dovezen a kdy má montáž proběhnout...',
    scaleQuestions: {
      unitLabel: 'Délka kuchyňské linky',
      presets: [
        'Menší linka do 2,5 metru',
        'Standardní linka 2,5 až 4 metry',
        'Rohová nebo velká kuchyně nad 4 metry',
        'Pouze montáž a výřez pracovní desky',
      ],
    },
  },
  {
    id: 'bathroom',
    group: 'interior_group',
    label: 'Rekonstrukce koupelny a bytového jádra',
    shortDesc: 'Kompletní koupelna na klíč, přestavba panelového jádra, výměna vany za sprchový kout a rekonstrukce WC',
    providerTarget: 'company_only',
    creditCost: 25,
    supportsFloorplan: true,
    subTasks: [
      'Kompletní rekonstrukce koupelny na klíč včetně rozvodů',
      'Vybourání umakartového jádra a nové vyzdění z Ytongu',
      'Výměna vany za bezbariérový walk-in sprchový kout',
      'Rekonstrukce samostatného WC vč. instalace Geberitu',
      'Montáž koupelnového nábytku a zrcadla s osvětlením',
    ],
    placeholderTitle: 'Např. Rekonstrukce panelové koupelny a WC na klíč',
    placeholderDesc:
      'Popište rozměry koupelny, zda preferujete vanu či sprchový kout a zda již máte vybrané obklady...',
    scaleQuestions: {
      unitLabel: 'Rozsah koupelny',
      presets: [
        'Samostatné WC / výměna sanity',
        'Menší koupelna v bytě (do 4 m²)',
        'Panelové jádro (koupelna + WC)',
        'Prostorná rodinná koupelna (>6 m²)',
      ],
    },
  },
  {
    id: 'tiling',
    group: 'interior_group',
    label: 'Obkladačské práce a pokládka dlažby',
    shortDesc: 'Obložení koupelny, WC a kuchyně, pokládka keramické dlažby, velkoformátové obklady a jolly rohy',
    providerTarget: 'company_only',
    creditCost: 18,
    supportsFloorplan: true,
    subTasks: [
      'Pokládka keramické a gresové dlažby v interiéru',
      'Obklady stěn v koupelně, na WC a za kuchyňskou linkou',
      'Pokládka moderních velkoformátových obkladů (až 120×60 cm)',
      'Kamenické rohy (jolly rohy broušené pod úhlem 45°)',
      'Aplikace hydroizolační stěrky a těsnicích pásek v rozích',
    ],
    placeholderTitle: 'Např. Pokládka velkoformátové dlažby v chodbě a obkladu v koupelně (35 m²)',
    placeholderDesc:
      'Uveďte rozměry obkladů/dlažby (např. 60×60 cm), celkovou plochu v m² a zda je podklad vyrovnaný...',
    scaleQuestions: {
      unitLabel: 'Celková plocha obkladů a dlažeb',
      presets: [
        'Menší plocha / za linkou (do 10 m²)',
        'Běžná koupelna (10 až 30 m²)',
        'Větší plocha nebo celý byt (30 až 70 m²)',
        'Velká realizace nad 70 m²',
      ],
    },
  },
  {
    id: 'flooring',
    group: 'interior_group',
    label: 'Podlahářství – vinyl, laminát, dřevo a koberce',
    shortDesc: 'Pokládka lepeného i click vinylu, plovoucích laminátových podlah, PVC lina, koberců a obvodových lišt',
    providerTarget: 'company_only',
    creditCost: 12,
    supportsFloorplan: true,
    subTasks: [
      'Pokládka plovoucího click vinylu nebo laminátu na podložku',
      'Pokládka celoplošně lepeného vinylu',
      'Pokládka zátěžových koberců a PVC (lina) včetně lepení',
      'Montáž obvodových soklových lišt a přechodových profilů',
      'Drobné vyrovnání podkladu před pokládkou',
    ],
    placeholderTitle: 'Např. Pokládka click vinylové podlahy v bytě 55 m² včetně lištování',
    placeholderDesc:
      'Uveďte jaký typ krytiny budete pokládat, zda máte nakoupené lišty a v jakém stavu je stávající podklad...',
    scaleQuestions: {
      unitLabel: 'Plocha podlah k pokládce',
      presets: [
        '1 místnost (do 20 m²)',
        '2 až 3 místnosti (20 až 55 m²)',
        'Celý byt / rodinný dům (55 až 120 m²)',
        'Velká plocha nad 120 m²',
      ],
    },
  },
  {
    id: 'parquet_renovation',
    group: 'interior_group',
    label: 'Renovace a broušení dřevěných parket a schodů',
    shortDesc: 'Bezprašné broušení dřevěných parket a prkenných podlah, tmelení spár, lakování, olejování a renovace schodů',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Hrubé a jemné bezprašné broušení parket pásovou bruskou',
      'Celoplošné tmelení spár tmelem smíchaným s dřevěným prachem',
      'Aplikace 3 vrstev vysoce odolného laku nebo tvrdého voskového oleje',
      'Výměna uvolněných nebo poškozených parketových vlysů',
      'Renovace a broušení dřevěných nášlapů schodiště',
    ],
    placeholderTitle: 'Např. Bezprašné přebroušení starých parket, tmelení a lakování ve dvou pokojích (42 m²)',
    placeholderDesc:
      'Uveďte v jakém stavu parkety jsou (např. zda se hýbou, zda na nich bylo lepidlo) a požadovaný finální finiš (lak/olej)...',
    scaleQuestions: {
      unitLabel: 'Plocha parket k renovaci',
      presets: [
        'Jeden pokoj (do 20 m²)',
        'Dva až tři pokoje (20 až 50 m²)',
        'Celý byt nebo dům (50 až 100 m²)',
        'Dřevěné schodiště',
      ],
    },
  },
  {
    id: 'carpentry',
    group: 'interior_group',
    label: 'Truhlářství a vestavěné skříně na míru',
    shortDesc: 'Vestavěné skříně na míru, šatny, knihovny, TV stěny, jídelní stoly, postele a dřevěné schody z masivu',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Vestavěná šatní skříň na míru s posuvnými či křídlovými dveřmi',
      'Vybavení samostatné šatny policovými systémy a zásuvkami',
      'Zakázková TV stěna, knihovna nebo pracovní stůl',
      'Výroba jídelního stolu z masivního dřeva',
      'Dřevěné schodiště, obklad betonových stupňů a dřevěná madla',
    ],
    placeholderTitle: 'Např. Vestavěná skříň na míru do ložnice s posuvnými zrcadlovými dveřmi',
    placeholderDesc:
      'Uveďte rozměry otvoru (šířka × výška × hloubka), preferovaný materiál (lamino, dýha) a vnitřní rozložení...',
    scaleQuestions: {
      unitLabel: 'Rozsah truhlářské zakázky',
      presets: [
        '1 vestavěná skříň',
        '2 a více skříní / celá šatna',
        'Nábytek do celého pokoje na míru',
        'Dřevěné schodiště / masivní stůl',
      ],
    },
  },
  {
    id: 'reconstruction',
    group: 'interior_group',
    label: 'Celková rekonstrukce bytu nebo domu na klíč',
    shortDesc: 'Kompletní přestavba bytu či domu (dispozice, elektro, voda, koupelna, podlahy, omítky) jednou firmou',
    providerTarget: 'company_only',
    creditCost: 30,
    supportsFloorplan: true,
    subTasks: [
      'Kompletní rekonstrukce bytu na klíč s koordinací všech profesí',
      'Celková přestavba staršího rodinného domu',
      'Vybourání a nová dispozice příček podle projektu',
      'Kompletní nová technická infrastruktura (elektro, voda, odpady, topení)',
      'Finální zednické práce, obklady, podlahy a dveře',
    ],
    placeholderTitle: 'Např. Kompletní rekonstrukce bytu 3+1 (72 m²) na klíč v cihlovém domě',
    placeholderDesc:
      'Popište celkovou plochu nemovitosti, současný stav a co vše očekáváte, že zhotovitel zajistí...',
    scaleQuestions: {
      unitLabel: 'Velikost rekonstruované nemovitosti',
      presets: [
        'Menší byt 1+kk / 2+kk (do 50 m²)',
        'Byt 3+1 / 4+kk (50 až 90 m²)',
        'Velký byt nad 90 m²',
        'Celý rodinný dům',
      ],
    },
  },

  // ─── 5. 🏗️ STŘECHY, OKNA, DVEŘE, KOMINÍCI & KOVY (7 KATEGORIÍ) ────────────
  {
    id: 'roofing',
    group: 'roof_windows_group',
    label: 'Střechy, pokrývači a opravy zatékání',
    shortDesc: 'Kompletní nová střecha, výměna střešní krytiny (tašky, plech, šindel), hydroizolace ploché střechy a opravy zatékání',
    providerTarget: 'company_only',
    creditCost: 25,
    supportsFloorplan: false,
    subTasks: [
      'Kompletní realizace nové šikmé nebo ploché střechy',
      'Výměna staré krytiny za pálené/betonové tašky nebo plech',
      'Hydroizolace a zateplení ploché střechy (asfaltové pásy, PVC fólie)',
      'Lokální oprava zatékání do střechy a výměna prasklých tašek',
      'Montáž střešních oken (Velux, Roto, Fakro)',
    ],
    placeholderTitle: 'Např. Výměna střešní krytiny za plechovou na sedlové střeše RD (160 m²)',
    placeholderDesc:
      'Uveďte typ střechy (sedlová, valbová, plochá), orientační plochu v m² a současný stav krovu...',
    scaleQuestions: {
      unitLabel: 'Plocha střechy',
      presets: [
        'Rychlá oprava zatékání / pár tašek',
        'Garáž nebo přístřešek (do 50 m²)',
        'Běžný rodinný dům (100 až 220 m²)',
        'Velká střecha nad 220 m²',
      ],
    },
  },
  {
    id: 'carpentry_roof_pergola',
    group: 'roof_windows_group',
    label: 'Tesařské práce, krovy, pergoly a přístřešky',
    shortDesc: 'Stavba a oprava krovu, dřevěné zahradní pergoly, přístřešky na auta (carport), dřevěné terasy a altány',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: false,
    subTasks: [
      'Výroba a montáž nového dřevěného krovu střechy',
      'Oprava a zpevnění poškozených trámů stávajícího krovu',
      'Stavba dřevěné zahradní pergoly nebo altánu',
      'Zastřešený dřevěný přístřešek pro auto (carport)',
      'Stavba venkovní dřevěné terasy z modřínu nebo exotického dřeva',
    ],
    placeholderTitle: 'Např. Stavba dřevěné pergoly k rodinnému domu s polykarbonátovým zastřešením',
    placeholderDesc:
      'Uveďte rozměry pergoly nebo krovu, zda preferujete hranoly z masivu či lepené BSH a zda máte základy...',
    scaleQuestions: {
      unitLabel: 'Typ dřevostavby',
      presets: [
        'Zahradní pergola nebo altán',
        'Přístřešek na auto (carport)',
        'Dřevěná terasa',
        'Krov rodinného domu',
      ],
    },
  },
  {
    id: 'tinsmith_gutters',
    group: 'roof_windows_group',
    label: 'Klempířské práce, okapy, svody a oplechování',
    shortDesc: 'Montáž a výměna okapů a svodů, oplechování komínů, úžlabí, štítů, atik, okenních parapetů a čištění okapů',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Montáž nebo výměna okapových žlabů a svodových rour (pozink, hliník, titanzinek)',
      'Oplechování komína a střešních prostupů',
      'Montáž střešního úžlabí, závětrných lišt a atiky',
      'Výroba a montáž venkovních parapetů',
      'Čištění ucpaných okapů a oprava netěsností spojů',
    ],
    placeholderTitle: 'Např. Výměna okapů a svodů na rodinném domě (délka cca 28 m)',
    placeholderDesc:
      'Uveďte délku žlabů, výšku budovy (kolik pater) a preferovaný materiál (barvený hliník, pozink, titanzinek)...',
    scaleQuestions: {
      unitLabel: 'Rozsah klempířských prací',
      presets: [
        'Drobné oplechování komína / parapety',
        'Okapy na menším objektu / chatě',
        'Kompletní okapový systém na rodinném domě',
        'Kompletní falcovaná střecha z plechu',
      ],
    },
  },
  {
    id: 'chimney_sweeps',
    group: 'roof_windows_group',
    label: 'Kominictví, vložkování a revize komínů',
    shortDesc: 'Pravidelné čištění a kontrola spalinových cest se zprávou, vložkování nerezem, frézování a stavba komínů',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Pravidelná roční kontrola a čištění spalinových cest se zprávou',
      'Vložkování komína nerezovou nebo plastovou vložkou',
      'Frézování zadehtovaného nebo úzkého komínového průduchu',
      'Stavba nového nerezového třísložkového fasádního komína',
      'Kolaudační revizní zpráva spalinové cesty pro nový kotel / krb',
    ],
    placeholderTitle: 'Např. Pravidelná kontrola a vyčištění komína na tuhá paliva s vystavením zprávy',
    placeholderDesc:
      'Uveďte typ spotřebiče (krb, kotel na dřevo/plyn), výšku komína a zda máte bezpečný přístup na střechu...',
    scaleQuestions: {
      unitLabel: 'Požadovaná kominická služba',
      presets: [
        'Pravidelná roční kontrola a čištění komína',
        'Vložkování komína pro nový kotel/krb',
        'Frézování zadehtovaného komína',
        'Stavba nového fasádního komína',
      ],
    },
  },
  {
    id: 'windows_doors',
    group: 'roof_windows_group',
    label: 'Okna, vchodové a interiérové dveře a zárubně',
    shortDesc: 'Výměna plastových, hliníkových i dřevěných oken, osazení obložkových zárubní a vchodových bezpečnostních dveří',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: true,
    subTasks: [
      'Dodávka a montáž plastových, dřevěných nebo hliníkových oken',
      'Montáž obložkových zárubní a interiérových dveří',
      'Výměna vchodových nebo bezpečnostních dveří do bytu/domu',
      'Montáž posuvných dveří do pouzdra či po stěně',
      'Demontáž a ekologická likvidace starých oken a dveří',
    ],
    placeholderTitle: 'Např. Dodávka a montáž 5 ks interiérových dveří s obložkovou zárubní',
    placeholderDesc:
      'Uveďte počet oken nebo dveří, zda máte hotové stavební otvory a zda požadujete i likvidaci starých...',
    scaleQuestions: {
      unitLabel: 'Počet oken a dveří',
      presets: [
        '1 až 2 kusy',
        '3 až 6 kusů (běžný byt)',
        '7 a více kusů (celý rodinný dům)',
        'Výměna hlavních vchodových dveří',
      ],
    },
  },
  {
    id: 'blinds_shading',
    group: 'roof_windows_group',
    label: 'Žaluzie, předokenní rolety, markýzy a sítě',
    shortDesc: 'Venkovní žaluzie Z90, předokenní hliníkové rolety, vnitřní žaluzie Den/Noc, markýzy a sítě proti hmyzu',
    providerTarget: 'company_only',
    creditCost: 10,
    supportsFloorplan: true,
    subTasks: [
      'Montáž venkovních žaluzií s motorickým ovládáním',
      'Instalace předokenních rolet (do překladu nebo dodatečných)',
      'Vnitřní žaluzie, látkové rolety a rolety Den a Noc',
      'Výroba a osazení pevných i otevíravých sítí proti hmyzu',
      'Montáž výsuvné terasové markýzy nebo screenových clon',
    ],
    placeholderTitle: 'Např. Montáž venkovních žaluzií s elektrickým pohonem na 4 okna RD',
    placeholderDesc:
      'Uveďte orientační rozměry oken, zda je na místě připravena elektroinstalace a preferovaný typ stínění...',
    scaleQuestions: {
      unitLabel: 'Počet stíněných oken / plocha',
      presets: [
        '1 až 3 okna / síťky',
        '4 až 8 oken (celý byt/dům)',
        'Velká terasová markýza / pergola',
        'Pouze oprava a seřízení stávajících žaluzií',
      ],
    },
  },
  {
    id: 'locksmith_metalwork',
    group: 'roof_windows_group',
    label: 'Zámečnictví, kovovýroba, svařování a zábradlí',
    shortDesc: 'Výroba vjezdových kovových bran, plotových dílců, ocelových schodišť, zábradlí na míru a zakázkové svařování',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Výroba a montáž posuvné nebo křídlové vjezdové brány',
      'Zámečnická výroba kovového zábradlí (schodiště, balkon, francouzská okna)',
      'Výroba ocelového schodiště na míru',
      'Svařování ocelových konstrukcí, mříží a přístřešků',
      'Oprava nebo úprava kovových konstrukcí na místě',
    ],
    placeholderTitle: 'Např. Výroba a montáž samonosné posuvné brány s pohonem a brankou',
    placeholderDesc:
      'Uveďte rozměry (délka/výška), povrchovou úpravu (žárový zinek, prášková barva) a zda chcete i pohon...',
    scaleQuestions: {
      unitLabel: 'Druh zámečnického výrobku',
      presets: [
        'Zábradlí na balkon nebo schody',
        'Vjezdová brána a branka',
        'Ocelové schodiště nebo mříže',
        'Zakázkové svařování a konstrukce',
      ],
    },
  },

  // ─── 6. 🎨 MALOVÁNÍ, MONTÁŽ NÁBYTKU & HODINOVÝ MANŽEL (7 KATEGORIÍ) ─────────
  {
    id: 'painting',
    group: 'finishing',
    label: 'Malování pokojů, bytů, domů a kanceláří',
    shortDesc: 'Výmalba pokojů na bílo i v barvě, škrábání staré malby, sádrování děr, penetrace a zakrytí nábytku',
    providerTarget: 'both',
    creditCost: 10,
    supportsFloorplan: true,
    subTasks: [
      'Výmalba stěn a stropů na bílo (kvalitní otěruvzdornou barvou)',
      'Barevná výmalba stěn dle vzorníku barev',
      'Škrábání staré vrstvy malby a hloubková penetrace podkladu',
      'Sádrování, akrylování prasklin a tmelení nerovností',
      'Důkladné zakrytí nábytku a podlah fóliemi a úklid po malování',
    ],
    placeholderTitle: 'Např. Výmalba zařízeného bytu 3+kk na bílo včetně zakrytí nábytku',
    placeholderDesc:
      'Uveďte podlahovou plochu nebo počet místností, stav stěn (zda se musí škrábat) a zda je byt zařízen...',
    scaleQuestions: {
      unitLabel: 'Velikost malovaného prostoru',
      presets: [
        '1 pokoj (do 20 m²)',
        'Byt 2+kk / 3+kk (40 až 80 m²)',
        'Celý rodinný dům (nad 80 m²)',
        'Kanceláře nebo komerční prostory',
      ],
    },
  },
  {
    id: 'coating_lacquering',
    group: 'finishing',
    label: 'Natěračské práce – dveře, okna, radiátory, ploty',
    shortDesc: 'Nátěry dveří a kovových zárubní, renovace dřevěných oken, nátěry radiátorů, plotů, pergol a podbití',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Renovační nátěry kovových zárubní (futer) a dveří',
      'Nátěry radiátorů a topenářských trubek',
      'Renovace a nový nátěr dřevěných kastlových nebo eurooken',
      'Ochranný nátěr dřevěného plotu, pergoly nebo podbití střechy',
      'Odmaštění, obroušení rzi a nátěr kovového zábradlí či střechy',
    ],
    placeholderTitle: 'Např. Obroušení a nový nátěr 6 ks kovových zárubní v bytě',
    placeholderDesc:
      'Uveďte o jaký předmět k nátěru se jedná, počet kusů a zda je povrch potřeba nejprve obrousit od rzi/staré barvy...',
    scaleQuestions: {
      unitLabel: 'Rozsah natěračských prací',
      presets: [
        'Nátěr 1 až 4 kusů zárubní / dveří',
        'Nátěry radiátorů v celém bytě',
        'Dřevěný plot nebo pergola na zahradě',
        'Podbití střechy rodinného domu',
      ],
    },
  },
  {
    id: 'wallpapering_stucco',
    group: 'finishing',
    label: 'Lepení tapet a dekorační stěrky',
    shortDesc: 'Lepení vliesových, vinylových i fototapet, odstraňování starých tapet, benátský štuk a betonové stěrky',
    providerTarget: 'company_only',
    creditCost: 12,
    supportsFloorplan: true,
    subTasks: [
      'Lepení vliesových a vinylových tapet se sesazováním vzoru',
      'Lepení velkoformátových fototapet na míru',
      'Aplikace moderní betonové stěrky (obývák, krb, koupelna)',
      'Aplikace benátského štuku a designových metalických stěrek',
      'Šetrné stržení starých tapet a penetrace stěn',
    ],
    placeholderTitle: 'Např. Nanesení betonové stěrky na pohledovou stěnu za TV (12 m²)',
    placeholderDesc:
      'Uveďte plochu stěny v m², zda se jedná o tapetu nebo stěrku a zda máte materiál již zakoupený...',
    scaleQuestions: {
      unitLabel: 'Plocha pro tapetování či stěrku',
      presets: [
        'Jedna dekorativní stěna (do 15 m²)',
        'Dvě stěny nebo celá místnost (15 až 35 m²)',
        'Více místností / stěrka v koupelně',
        'Odstranění starých tapet v celém bytě',
      ],
    },
  },
  {
    id: 'furniture_assembly',
    group: 'finishing',
    label: 'Skládání a montáž nábytku (IKEA, Jysk apod.)',
    shortDesc: 'Složení šatních skříní (PAX apod.), manželských postelí, komod, obývacích stěn, stolů a dětských pokojů',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: true,
    subTasks: [
      'Montáž šatních skříní s posuvnými i pantovými dveřmi (IKEA PAX)',
      'Sestavení manželské postele včetně roštů a čela',
      'Montáž komod, zásuvkových dílů a nočních stolků',
      'Sestavení obývací stěny, TV stolku a knihoven',
      'Demontáž starého nábytku před stěhováním nebo výměnou',
    ],
    placeholderTitle: 'Např. Složení šatní skříně PAX 200 cm a postele z IKEA',
    placeholderDesc:
      'Uveďte konkrétní modely a počet kusů nábytku a zda je potřeba skříň ukotvit do zdi...',
    scaleQuestions: {
      unitLabel: 'Množství nábytku k montáži',
      presets: [
        '1 kus (skříň, postel nebo komoda)',
        '2 až 4 kusy nábytku',
        'Kompletní vybavení jednoho pokoje',
        'Kompletní vybavení celého bytu/kanceláře',
      ],
    },
  },
  {
    id: 'drilling_hanging',
    group: 'finishing',
    label: 'Vrtání do zdi a věšení polic, TV, zrcadel a garnýží',
    shortDesc: 'Vrtání do betonu/cihly/obkladů, montáž nástěnných držáků TV, věšení kuchyňských skříněk, polic a obrazů',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Vrtání do tvrdého panelu, plné cihly i sádrokartonu',
      'Montáž držáku na TV na zeď včetně zavěšení televize',
      'Uchycení polic, zrcadel, obrazů a nástěnných věšáků',
      'Montáž stropních a nástěnných garnýží a kolejniček na záclony',
      'Vrtání do keramických obkladů v koupelně bez prasknutí kachličky',
    ],
    placeholderTitle: 'Např. Přivrtání držáku na TV do panelu, dvou poliček a garnýže',
    placeholderDesc:
      'Uveďte seznam věcí k zavěšení a materiál stěn (panel, cihla, sádrokarton)...',
    scaleQuestions: {
      unitLabel: 'Počet předmětů k zavěšení',
      presets: [
        '1 až 3 věci (rychlá návštěva)',
        '4 až 8 předmětů',
        'Více než 8 předmětů / celé zařizování bytu',
      ],
    },
  },
  {
    id: 'handyman',
    group: 'finishing',
    label: 'Hodinový manžel a drobné opravy v domácnosti',
    shortDesc: 'Seřízení dvířek a pantů, přetmelení silikonu u vany/dřezu, montáž lišt, prahů, výměna věšáků a údržba',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Seřízení nábytkových pantů, zásuvkových pojezdů a dvířek',
      'Odstranění zašlého a aplikace nového sanitárního silikonu',
      'Přilepení nebo přivrtání přechodových lišt a dveřních prahů',
      'Drobné opravy poškozeného nábytku a výměna úchytek',
      'Souhrnný seznam drobných oprav a údržby v bytě',
    ],
    placeholderTitle: 'Např. Půldenní hodinový manžel – seřízení skříněk, nový silikon a lišty',
    placeholderDesc:
      'Spište seznam všech drobných oprav, které v domácnosti potřebujete vyřešit...',
    scaleQuestions: {
      unitLabel: 'Odhadovaná délka prací',
      presets: [
        'Rychlá oprava (1 až 2 hodiny)',
        'Půldenní návštěva (3 až 5 hodin)',
        'Celodenní seznam oprav (6 až 8 hodin)',
      ],
    },
  },
  {
    id: 'locks_security_doors',
    group: 'finishing',
    label: 'Výměna zámků, vložek FAB, klik a kování',
    shortDesc: 'Výměna cylindrických vložek (FAB), montáž bezpečnostního kování, seřízení vchodových dveří a opravy zámků',
    providerTarget: 'company_only',
    creditCost: 10,
    supportsFloorplan: false,
    subTasks: [
      'Výměna cylindrické bezpečnostní vložky (FAB) ve dveřích',
      'Montáž bezpečnostního kování se štítem a překrytím vložky',
      'Výměna zadlabacího zámku ve dveřích',
      'Seřízení vchodových dveří (drhnutí o práh, nedoléhání těsnění)',
      'Montáž přídavného bezpečnostního zámku nebo rozvory',
    ],
    placeholderTitle: 'Např. Výměna cylindrické vložky FAB za bezpečnostní a seřízení vchodových dveří',
    placeholderDesc:
      'Uveďte typ dveří (bezpečnostní, běžné dřevěné, plastové) a zda potřebujete dodat i novou vložku...',
    scaleQuestions: {
      unitLabel: 'Rozsah prací se zámky',
      presets: [
        'Výměna 1 vložky nebo kliky',
        'Kompletní zámek + bezpečnostní kování',
        'Seřízení a oprava vchodových dveří',
        'Výměna zámků v celém objektu',
      ],
    },
  },

  // ─── 7. 🌳 ZAHRADA, STROMY, PLOTY, DLAŽBY & VÝKOPY (9 KATEGORIÍ) ────────────
  {
    id: 'garden_mowing',
    group: 'garden',
    label: 'Sekání zahrady, trávy a vyžínání křovinořezem',
    shortDesc: 'Posekání trávníku sekačkou, vysekání vysoké trávy a náletů křovinořezem, sekání svahů a odvoz trávy',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Sekání udržovaného trávníku zahradní sekačkou se sběrem',
      'Vysekání přerostlé vysoké trávy a bodláčí křovinořezem',
      'Sekání prudkých svahů a špatně přístupných míst',
      'Hrabání a naložení posekané trávy do bioodpadu',
      'Vertikutace (odmechování) a hnojení trávníku',
    ],
    placeholderTitle: 'Např. Posekání udržované zahrady 700 m² a odvoz posekané trávy',
    placeholderDesc:
      'Uveďte plochu pozemku v m², výšku trávy a zda máte na místě k dispozici kompost nebo odvoz...',
    scaleQuestions: {
      unitLabel: 'Plocha pozemku k posekání',
      presets: [
        'Malá zahrádka (do 300 m²)',
        'Běžná zahrada (300 až 800 m²)',
        'Velký pozemek (800 až 2 000 m²)',
        'Louka / sad nad 2 000 m²',
      ],
    },
  },
  {
    id: 'garden_hedges_weeding',
    group: 'garden',
    label: 'Stříhání živých plotů, pletí a údržba zahrady',
    shortDesc: 'Stříhání a tvarování živých plotů a tújí, pletí záhonů, mulčování kůrou, hrabání listí a vertikutace',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Stříhání a tvarování tújí a listnatých živých plotů',
      'Důkladné vypletí okrasných i zeleninových záhonů',
      'Navození a rozprostření mulčovací kůry nebo štěrku',
      'Hrabání spadaného listí a jarní/podzimní očista zahrady',
      'Zmlazovací řez keřů a okrasných dřevin',
    ],
    placeholderTitle: 'Např. Zastřižení živého plotu z tújí (délka 30 m, výška 2 m) a vypletí záhonů',
    placeholderDesc:
      'Uveďte délku a výšku živého plotu a zda je potřeba zajistit odvoz ostříhaných větví...',
    scaleQuestions: {
      unitLabel: 'Rozsah zahradnické práce',
      presets: [
        'Půldenní údržba (do 4 hodin)',
        'Celodenní práce na zahradě',
        'Stříhání velkého živého plotu (>30 m)',
        'Pravidelná sezónní péče',
      ],
    },
  },
  {
    id: 'garden_planting_design',
    group: 'garden',
    label: 'Zakládání trávníků, výsadba zahrad a závlahy',
    shortDesc: 'Založení nového trávníku (výsev i travní koberce), výsadba vzrostlých stromů a automatické závlahy',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Příprava podloží a položení hotových travních koberců',
      'Založení nového trávníku výsevem včetně kultivace půdy',
      'Výsadba okrasných stromů, keřů a živých plotů',
      'Instalace automatického závlahového systému (Hunter, Rain Bird)',
      'Modelace terénu a zakládání okrasných skalek a záhonů',
    ],
    placeholderTitle: 'Např. Položení travních koberců na ploše 250 m² včetně srovnání terénu',
    placeholderDesc:
      'Uveďte plochu pozemku v m², požadovaný způsob založení (koberec/výsev) a zda chcete i závlahu...',
    scaleQuestions: {
      unitLabel: 'Plocha pro nový trávník / výsadbu',
      presets: [
        'Malá plocha (do 100 m²)',
        'Střední zahrada (100 až 300 m²)',
        'Velká zahrada (300 až 800 m²)',
        'Rozsáhlý park / pozemek nad 800 m²',
      ],
    },
  },
  {
    id: 'wood_chopping',
    group: 'garden',
    label: 'Řezání, štípání a skládání palivového dřeva',
    shortDesc: 'Nařezání klád motorovou pilou na špalky, rozštípání sekerou či štípačkou a narovnání do dřevníku',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Nařezání metrového dřeva nebo klád motorovou pilou na polena',
      'Štípání špalků sekerou nebo hydraulickou štípačkou',
      'Povození dřeva kolečkem a úhledné narovnání do dřevníku',
      'Úklid pilin, třísek a odřezků po štípání',
    ],
    placeholderTitle: 'Např. Nařezání, naštípání a složení 10 prostorových metrů palivového dřeva',
    placeholderDesc:
      'Uveďte množství dřeva v prms, zda jde o měkké či tvrdé dřevo a zda máte vlastní nářadí...',
    scaleQuestions: {
      unitLabel: 'Množství dřeva ke zpracování',
      presets: [
        'Do 5 prostorových metrů (PRMS)',
        '5 až 12 prostorových metrů',
        '13 až 25 prostorových metrů',
        'Více než 25 metrů dřeva',
      ],
    },
  },
  {
    id: 'tree_felling_expert',
    group: 'garden',
    label: 'Rizikové kácení stromů a odborný prořez',
    shortDesc: 'Postupné stromolezecké kácení vzrostlých stromů u budov a drátů, kácení z vysokozdvižné plošiny a prořezy',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: false,
    subTasks: [
      'Postupné rizikové kácení stromu ze stoupacích želez a lana',
      'Kácení vzrostlého stromu z vysokozdvižné pracovní plošiny',
      'Zdravotní, bezpečnostní a redukční prořez větví v koruně',
      'Kácení stromů přímo nad střechou, dráty elektřiny či plotem',
      'Zajištění bezpečného spouštění odřezaných větví na laně',
    ],
    placeholderTitle: 'Např. Rizikové pokácení vzrostlého smrku 15 m v blízkosti střechy domu',
    placeholderDesc:
      'Uveďte druh stromu, přibližnou výšku, zda stojí blízko zástavby a zda požadujete i likvidaci větví...',
    scaleQuestions: {
      unitLabel: 'Počet a náročnost stromů',
      presets: [
        '1 strom se snadným přístupem',
        '1 vzrostlý strom u domu (rizikové)',
        '2 až 4 stromy',
        'Odborný prořez korun stromů v sadu',
      ],
    },
  },
  {
    id: 'stump_grinding_chipping',
    group: 'garden',
    label: 'Frézování pařezů a štěpkování větví',
    shortDesc: 'Odfrézování pařezů pod úroveň terénu, drcení a štěpkování větví po prořezu a odvoz bioodpadu',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Odfrézování pařezu pařezovou frézou do hloubky 15–30 cm pod terén',
      'Drcení a štěpkování větví výkonným motorovým štěpkovačem',
      'Zanechání štěpky na mulčování nebo zajištění odvozu',
      'Zasypání a urovnání terénu po frézování pařezu',
    ],
    placeholderTitle: 'Např. Odfrézování 3 pařezů po kácení a seštěpkování větví na zahradě',
    placeholderDesc:
      'Uveďte průměr pařezů v centimetrech a zda je na místo možný vjezd zahradní technikou...',
    scaleQuestions: {
      unitLabel: 'Počet pařezů a objem větví',
      presets: [
        '1 až 2 menší pařezy (do 40 cm)',
        '1 velký mohutný pařez (>50 cm)',
        '3 a více pařezů',
        'Samostatné štěpkování hromady větví',
      ],
    },
  },
  {
    id: 'fencing_gates',
    group: 'garden',
    label: 'Stavba plotů, pletiva, betonové ploty a brány',
    shortDesc: 'Montáž pletivových plotů, 2D/3D svařovaných panelů, stavba betonových plotů, ztraceného bednění a bran',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Montáž klasického pletivového plotu včetně sloupků a vzpěr',
      'Montáž pevných 2D a 3D plotových panelů na sloupky',
      'Stavba skládaného betonového plotu z desek a sloupků',
      'Vyzdění plotové podezdívky ze ztraceného bednění s čepicemi',
      'Osazení vchodové branky a vjezdové brány',
    ],
    placeholderTitle: 'Např. Stavba pletivového plotu s podhrabovými deskami (délka 45 m)',
    placeholderDesc:
      'Uveďte celkovou délku plotu v metrech, požadovanou výšku a typ plotu (pletivo, 3D panely, beton)...',
    scaleQuestions: {
      unitLabel: 'Délka plotu k realizaci',
      presets: [
        'Menší úsek plotu (do 20 m)',
        'Běžný plot (20 až 50 m)',
        'Velký obvod pozemku (50 až 100 m)',
        'Rozsáhlé oplocení nad 100 m',
      ],
    },
  },
  {
    id: 'fencing_paving',
    group: 'garden',
    label: 'Pokládka zámkové dlažby, terasy a obrubníky',
    shortDesc: 'Zámková dlažba pro vjezdy, chodníky a stání aut, usazení obrubníků a palisád do betonu a štěrkový kufr',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: false,
    subTasks: [
      'Pokládka zámkové dlažby pro pojezd aut (tloušťka 8 cm) i chodníky (6 cm)',
      'Usazení silničních a zahradních obrubníků do betonového lože',
      'Hutnění a příprava nosných štěrkových podkladních vrstev (kufr)',
      'Pokládka velkoformátové betonové nebo keramické dlažby na terasu',
      'Zapískování spár křemičitým pískem a finální zavibrování',
    ],
    placeholderTitle: 'Např. Pokládka zámkové dlažby na vjezd a parkovací stání (65 m²)',
    placeholderDesc:
      'Uveďte plochu dlažby v m², účel (pojezdové stání pro auta vs. chodník) a zda je připraven výkop...',
    scaleQuestions: {
      unitLabel: 'Plocha zámkové dlažby',
      presets: [
        'Chodník nebo menší plocha (do 25 m²)',
        'Parkovací stání / vjezd (25 až 60 m²)',
        'Velká plocha kolem domu (60 až 140 m²)',
        'Velká realizace nad 140 m²',
      ],
    },
  },
  {
    id: 'excavation_wells',
    group: 'garden',
    label: 'Zemní práce, výkopy minibagrem, jímky a studny',
    shortDesc: 'Výkopy základových pásů, rýhy pro inženýrské sítě minibagrem, výkopy pro bazény, retenční nádrže, ČOV a studny',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: false,
    subTasks: [
      'Výkopy rýh pro inženýrské sítě (voda, kanalizace, elektřina, plyn)',
      'Výkopové práce pro retenční nádrže na dešťovku, jímky a ČOV',
      'Výkop stavební jámy pro zapuštěný bazén',
      'Srovnání terénu pozemku a svahování minibagrem',
      'Čištění, prohlubování nebo kopání studní',
    ],
    placeholderTitle: 'Např. Výkop rýhy pro kanalizační přípojku (25 m) a retenční nádrž minibagrem',
    placeholderDesc:
      'Uveďte rozsah výkopů (délka/hloubka), přístupnost pozemku pro techniku a co se má stát s vytěženou zeminou...',
    scaleQuestions: {
      unitLabel: 'Rozsah zemních prací',
      presets: [
        'Drobný výkop / rýha do 15 m',
        'Střední výkop (inženýrské sítě / jímka)',
        'Výkop pro bazén nebo retenční nádrž',
        'Kompletní terénní úpravy pozemku',
      ],
    },
  },

  // ─── 8. 🚚 STĚHOVÁNÍ, VYKLÍZENÍ & ÚKLID (7 KATEGORIÍ) ───────────────────────
  {
    id: 'moving_apartments',
    group: 'moving_cleaning_group',
    label: 'Stěhování bytů, domů a kanceláří (s dodávkou)',
    shortDesc: 'Kompletní přestěhování velkou stěhovací dodávkou, balicí servis, demontáž/montáž nábytku i těžká břemena',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Kompletní přestěhování bytu nebo domu prostornou dodávkou',
      'Zabalení nábytku do strečových a bublinkových fólií',
      'Demontáž nábytku před transportem a opětovné složení na místě',
      'Stěhování těžkých břemen (pianina, trezory, masivní stoly)',
      'Přestěhování kanceláří a firemních prostor',
    ],
    placeholderTitle: 'Např. Kompletní přestěhování bytu 2+1 z Prahy 4 do Prahy 8 se stěhovacím vozem',
    placeholderDesc:
      'Uveďte z jakého do jakého patra se stěhuje, zda je k dispozici výtah a zda požadujete i balení nábytku...',
    scaleQuestions: {
      unitLabel: 'Velikost stěhovaného prostoru',
      presets: [
        'Pár kusů nábytku nebo garsonka',
        'Byt 2+kk / 2+1',
        'Velký byt 3+1 / rodinný dům',
        'Kancelářské prostory',
      ],
    },
  },
  {
    id: 'moving_help',
    group: 'moving_cleaning_group',
    label: 'Brigáda na stěhování a nošení těžkých věcí',
    shortDesc: 'Fyzická výpomoc s vynesením gauče, pračky, lednice či krabic po schodech, posun nábytku v bytě',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Vynesení nebo snesení těžkých spotřebičů (pračka, lednice, sušička)',
      'Pomoc s nošením krabic a nábytku do patra bez výtahu',
      'Přemístění těžkých skříní a sedaček v rámci bytu',
      'Pomoc s nakládkou a vykládkou přistaveného vozu',
      'Fyzická výpomoc při stěhování na pár hodin',
    ],
    placeholderTitle: 'Např. Pomoc dvou brigádníků s vynesením sedačky a pračky do 3. patra',
    placeholderDesc:
      'Uveďte kolik pomocníků hledáte, co konkrétně se bude nosit a z/do kolikátého patra...',
    scaleQuestions: {
      unitLabel: 'Časový rozsah výpomoci',
      presets: [
        'Rychlá pomoc na 1 až 2 hodiny',
        'Výpomoc na půl dne (3 až 5 hodin)',
        'Celodenní pomoc při stěhování',
      ],
    },
  },
  {
    id: 'clearing_cellars',
    group: 'moving_cleaning_group',
    label: 'Vyklízení sklepů, půd, garáží a pozůstalostí',
    shortDesc: 'Kompletní vyklizení starého harampádí, nábytku a suti ze sklepa, půdy či garáže a odvoz na sběrný dvůr',
    providerTarget: 'both',
    creditCost: 10,
    supportsFloorplan: false,
    subTasks: [
      'Vyklizení sklepní kóje, společných prostor nebo garáže',
      'Vyklizení zaprášené půdy nebo zahradní kůlny',
      'Kompletní vyklizení bytu po pozůstalosti',
      'Naložení odpadu a odvoz dodávkou do sběrného dvora',
      'Přistavení velkoobjemového kontejneru a naložení odpadu',
    ],
    placeholderTitle: 'Např. Kompletní vyklizení sklepa 15 m² a odvoz starého nábytku na sběrný dvůr',
    placeholderDesc:
      'Popište množství věcí, o jaký prostor jde (sklep, půda) a zda požadujete i zajištění odvozu...',
    scaleQuestions: {
      unitLabel: 'Rozsah vyklízených prostor',
      presets: [
        'Sklepní kóje nebo garáž (do 15 m²)',
        'Půda nebo menší byt',
        'Celý rodinný dům / pozůstalost',
        'Přistavení kontejneru na odpad',
      ],
    },
  },
  {
    id: 'demolition_digging',
    group: 'moving_cleaning_group',
    label: 'Ruční výkopové práce a stavební brigáda',
    shortDesc: 'Ruční výkopy rýh krumpáčem a lopatou (kabely, voda, plot), nošení stavebního materiálu a suti do patra',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Ruční výkop rýhy pro elektro kabel, vodu nebo základy plotu',
      'Nošení pytlů se směsí, cihel a sádrokartonů do patra',
      'Snášení stavební suti v kbelících a nakládka do kontejneru',
      'Pomocné míchání betonu, malt a podávání materiálu',
      'Hrubý úklid staveniště po zednících',
    ],
    placeholderTitle: 'Např. Ruční výkop rýhy na kabel 20 m a pomocné zednické práce na víkend',
    placeholderDesc:
      'Uveďte druh práce, délku rýhy nebo váhu materiálu a kolik brigádníků na jak dlouho hledáte...',
    scaleQuestions: {
      unitLabel: 'Doba trvání pomocných prací',
      presets: [
        'Jednorázová výpomoc na den',
        'Víkendová stavební brigáda (2 dny)',
        'Týdenní výpomoc na stavbě',
        'Dlouhodobější pomocné práce',
      ],
    },
  },
  {
    id: 'home_cleaning',
    group: 'moving_cleaning_group',
    label: 'Pravidelný a generální úklid domácnosti',
    shortDesc: 'Jednorázový generální úklid bytu/domu, pravidelný týdenní úklid, precizní úklid kuchyně, koupelny a žehlení',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Jednorázový hloubkový generální úklid bytu nebo rodinného domu',
      'Pravidelný týdenní úklid (vysávání, vytírání, prach, koupelna)',
      'Důkladné čištění kuchyně (odmaštění linky, trouby a lednice)',
      'Desinfekce a odvápnění koupelny, sprchového koutu a toalety',
      'Žehlení prádla a převlékání lůžkovin',
    ],
    placeholderTitle: 'Např. Generální úklid bytu 3+kk po nájemnících',
    placeholderDesc:
      'Uveďte velikost bytu v m², zda máte vlastní čisticí prostředky nebo si je má uklízečka přinést...',
    scaleQuestions: {
      unitLabel: 'Velikost domácnosti a frekvence',
      presets: [
        'Menší byt (do 55 m²)',
        'Běžný byt 2+kk / 3+kk (55 až 85 m²)',
        'Velký byt / rodinný dům (>85 m²)',
        'Pravidelný týdenní úklid',
      ],
    },
  },
  {
    id: 'window_upholstery_cleaning',
    group: 'moving_cleaning_group',
    label: 'Mytí oken, žaluzií a čištění sedaček a koberců',
    shortDesc: 'Mytí oken, rámů, parapetů a žaluzií, mokré hloubkové tepování sedacích souprav, matrací a koberců',
    providerTarget: 'both',
    creditCost: 8,
    supportsFloorplan: false,
    subTasks: [
      'Mytí oken včetně rámů, parapetů a kliček',
      'Čištění vnitřních i venkovních okenních žaluzií',
      'Mokré extrakční tepování čalouněných sedacích souprav a křesel',
      'Hloubkové čištění koberců profesionálním extraktorem',
      'Tepování matrací a odstranění skvrn a zápachu',
    ],
    placeholderTitle: 'Např. Mytí 6 oken v bytě a vytepování rohové sedací soupravy',
    placeholderDesc:
      'Uveďte počet oken a velikost sedačky (např. rohová do L pro 4 osoby), případně typ koberců...',
    scaleQuestions: {
      unitLabel: 'Rozsah mytí nebo tepování',
      presets: [
        'Pouze mytí oken (do 6 oken)',
        'Mytí oken v celém rodinném domě',
        'Tepování sedačky / matrací',
        'Kombinace mytí oken a kompletního tepování',
      ],
    },
  },
  {
    id: 'post_renovation_cleaning',
    group: 'moving_cleaning_group',
    label: 'Úklid po rekonstrukci, stavbě a malování',
    shortDesc: 'Odstranění jemného stavebního prachu, umytí oken a podlah od barev, lepidel a tmelů před nastěhováním',
    providerTarget: 'both',
    creditCost: 10,
    supportsFloorplan: false,
    subTasks: [
      'Vysátí a odstranění jemného stavebního prachu průmyslovým vysavačem',
      'Odstranění zbytků barev, laků, omítek a silikonů z oken a rámů',
      'Mytí a odmaštění nových podlah, obkladů a sanitární keramiky',
      'Otření vnitřků skříněk a nábytku od prachu před nastěhováním',
      'Finální příprava nemovitosti k předání nebo k nastěhování',
    ],
    placeholderTitle: 'Např. Kompletní stavební úklid bytu 75 m² po malování a položení podlah',
    placeholderDesc:
      'Uveďte podlahovou plochu v m², jaké stavební práce v bytě probíhaly a termín nastěhování...',
    scaleQuestions: {
      unitLabel: 'Plocha uklízeného objektu',
      presets: [
        'Jedna až dvě místnosti po malování',
        'Celý byt po rekonstrukci (do 75 m²)',
        'Velký byt / novostavba domu (nad 75 m²)',
        'Komerční prostor po rekonstrukci',
      ],
    },
  },

  // ─── 9. 📐 PROJEKTY, DOZOR, REVIZE & SPECIÁLNÍ SLUŽBY (6 KATEGORIÍ) ────────
  {
    id: 'architect_interior',
    group: 'projects_special_group',
    label: 'Architekt, návrh interiéru a 3D vizualizace',
    shortDesc: 'Návrh nové dispozice bytu či domu, fotorealistické 3D vizualizace kuchyní, koupelen i celých interiérů',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: true,
    subTasks: [
      'Architektonická studie a návrh nové dispozice bytu nebo domu',
      'Fotorealistické 3D vizualizace interiéru (obývák, kuchyň, koupelna)',
      'Výběr materiálů, barevného schématu a nábytku na míru',
      'Zpracování prováděcí výkresové dokumentace pro truhláře',
      'Autorský dozor architekta při realizaci',
    ],
    placeholderTitle: 'Např. 3D návrh a vizualizace nového obývacího pokoje propojeného s kuchyní',
    placeholderDesc:
      'Popište velikost prostoru, styl který preferujete (skandinávský, moderní, industriální) a zda máte půdorys...',
    scaleQuestions: {
      unitLabel: 'Rozsah návrhu',
      presets: [
        'Jedna místnost (např. kuchyň / obývák)',
        'Koupelna a WC s kladečským plánem',
        'Kompletní interiér bytu',
        'Celý rodinný dům',
      ],
    },
  },
  {
    id: 'project_engineer',
    group: 'projects_special_group',
    label: 'Projektant – stavební povolení, statika, TZB',
    shortDesc: 'Projekt pro stavební povolení a ohlášení stavby, posudek statika při bourání příček, projekty TZB a PENB',
    providerTarget: 'company_only',
    creditCost: 25,
    supportsFloorplan: true,
    subTasks: [
      'Projektová dokumentace pro stavební povolení a ohlášení stavby',
      'Statický posudek (bourání nosné zdi, otvor v panelovém domě)',
      'Projekt technických zařízení budov – TZB (elektro, voda, plyn, topení)',
      'Průkaz energetické náročnosti budovy (PENB)',
      'Inženýring a vyřízení stavebního povolení na úřadech',
    ],
    placeholderTitle: 'Např. Statický posudek pro vybourání otvoru v nosné panelové zdi',
    placeholderDesc:
      'Uveďte o jaký typ nemovitosti jde, záměr (bourání zdi, novostavba, přístavba) a zda máte původní plány...',
    scaleQuestions: {
      unitLabel: 'Druh projektové činnosti',
      presets: [
        'Statický posudek pro otvor ve zdi',
        'Projekt rekonstrukce / přístavby',
        'Kompletní projekt novostavby pro povolení',
        'Projekt TZB nebo PENB',
      ],
    },
  },
  {
    id: 'building_supervisor',
    group: 'projects_special_group',
    label: 'Stavební dozor a kontrola nemovitosti před koupí',
    shortDesc: 'Technický dozor investora (TDI) na stavbě, přejímka bytu nebo novostavby a technická inspekce před koupí',
    providerTarget: 'company_only',
    creditCost: 20,
    supportsFloorplan: false,
    subTasks: [
      'Technický dozor investora (TDI) při výstavbě rodinného domu',
      'Přejímka dokončeného bytu nebo domu od developera (inspekce vad)',
      'Odborná technická prověrka nemovitosti před koupí (prověření vad)',
      'Kontrola kvality prováděných prací a souladu s projektem',
      'Pravidelné kontrolní dny a zápisy do stavebního deníku',
    ],
    placeholderTitle: 'Např. Odborná kontrola a přejímka novostavby bytu 3+kk od developera',
    placeholderDesc:
      'Uveďte lokalitu a typ nemovitosti, termín přejímky a zda požadujete sepsání protokolu vad...',
    scaleQuestions: {
      unitLabel: 'Typ požadovaného dozoru',
      presets: [
        'Jednorázová přejímka bytu / novostavby',
        'Inspekce technického stavu před koupí',
        'Pravidelný stavební dozor při rekonstrukci',
        'Technický dozor investora (TDI) celé stavby',
      ],
    },
  },
  {
    id: 'geodesist',
    group: 'projects_special_group',
    label: 'Geodetické práce a vyměření hranic pozemku',
    shortDesc: 'Vytyčení hranic pozemku pro stavbu plotu, geometrický plán pro kolaudaci a zápis do katastru nemovitostí',
    providerTarget: 'company_only',
    creditCost: 15,
    supportsFloorplan: false,
    subTasks: [
      'Vytyčení hranic pozemku v terénu (pro stavbu plotu apod.)',
      'Zhotovení geometrického plánu pro kolaudaci stavby',
      'Geometrický plán pro rozdělení pozemku nebo věcné břemeno',
      'Polohopisné a výškopisné zaměření pro projektanta',
      'Vytyčení stavby a základů rodinného domu',
    ],
    placeholderTitle: 'Např. Vytyčení hranice pozemku pro stavbu nového plotu (4 lomové body)',
    placeholderDesc:
      'Uveďte katastrální území, parcelní číslo pozemku a pro jaký účel geodetické zaměření potřebujete...',
    scaleQuestions: {
      unitLabel: 'Druh geodetických prací',
      presets: [
        'Vytyčení hranic pozemku pro plot',
        'Geometrický plán pro kolaudaci / katastr',
        'Zaměření polohopisu a výškopisu pro projekt',
        'Vytyčení základů stavby',
      ],
    },
  },
  {
    id: 'pest_control',
    group: 'projects_special_group',
    label: 'Likvidace vos, sršňů, štěnic a deratizace',
    shortDesc: 'Rychlá likvidace vosích a sršních hnízd, profesionální hubení štěnic, mravenců, švábů a deratizace hlodavců',
    providerTarget: 'company_only',
    creditCost: 12,
    supportsFloorplan: false,
    subTasks: [
      'Likvidace vosího nebo sršního hnízda (v podkroví, na fasádě, v zemi)',
      'Profesionální hubení štěnic se zárukou (postřik + dýmovnice)',
      'Dezinsekce mravenců, švábů, rusů a molů v domácnosti',
      'Deratizace myší, potkanů a krys v domě, sklepě či firmě',
      'Dezinfekce a ozonové čištění prostor po hlodavcích či plísních',
    ],
    placeholderTitle: 'Např. Rychlá likvidace vosího hnízda pod střechou rodinného domu',
    placeholderDesc:
      'Uveďte o jakého škůdce se jedná, kde se nachází (výška, přístupnost) a zda je nutný urgentní zásah...',
    scaleQuestions: {
      unitLabel: 'Druh škůdce a naléhavost',
      presets: [
        'Likvidace vosího / sršního hnízda',
        'Hubení štěnic v bytě',
        'Mravenci / švábi v kuchyni',
        'Deratizace hlodavců (myši, potkani)',
      ],
    },
  },
  {
    id: 'other',
    group: 'projects_special_group',
    label: 'Jiná zakázka nebo specifická výpomoc',
    shortDesc: 'Jakákoliv další odborná řemeslná, technická nebo manuální brigádnická práce dle vašeho popisu',
    providerTarget: 'both',
    creditCost: 10,
    supportsFloorplan: true,
    subTasks: [
      'Specifická řemeslná práce dle individuálního zadání',
      'Manuální brigáda nebo pomocné práce v domácnosti / na zahradě',
      'Kombinace více různých řemesel v jedné zakázce',
      'Montážní nebo servisní práce nespecifikované výše',
      'Konzultace nebo technické posouzení na místě',
    ],
    placeholderTitle: 'Např. Odborná oprava a renovace starožitné dřevěné truhly',
    placeholderDesc:
      'Popište prosím co nejpřesněji, co potřebujete zhotovit, opravit nebo s čím potřebujete pomoci...',
    scaleQuestions: {
      unitLabel: 'Orientační rozsah prací',
      presets: [
        'Drobná práce na 1 až 2 hodiny',
        'Půldenní až celodenní zakázka',
        'Větší projekt na více dní',
        'Pravidelná výpomoc',
      ],
    },
  },
]

export function getCategoryInfo(id: TradeCategory): TradeCategoryInfo {
  return TRADE_CATEGORIES.find((c) => c.id === id) || TRADE_CATEGORIES[TRADE_CATEGORIES.length - 1]
}

export function getInquiryCategories(
  inq: Pick<Inquiry, 'category' | 'categories'>
): TradeCategory[] {
  if (inq.categories && inq.categories.length > 0) {
    const unique: TradeCategory[] = []
    for (const c of inq.categories) {
      if (!unique.includes(c)) unique.push(c)
    }
    return unique
  }
  return [inq.category]
}

export function getRecommendedComplementaryCategories(
  selected: TradeCategory[]
): TradeCategoryInfo[] {
  const suggestionPool: TradeCategory[] = []
  const addSuggestions = (cats: TradeCategory[]) => {
    for (const c of cats) {
      if (!selected.includes(c) && !suggestionPool.includes(c)) {
        suggestionPool.push(c)
      }
    }
  }

  for (const catId of selected) {
    const info = getCategoryInfo(catId)
    if (info.group === 'electrical_group') {
      addSuggestions(['plumbing', 'masonry', 'drywall_attic', 'painting', 'kitchen', 'heating'])
    } else if (info.group === 'plumbing_heating_group') {
      addSuggestions(['electrical', 'bathroom', 'tiling', 'masonry', 'heating_boilers_pumps'])
    } else if (catId === 'kitchen' || catId === 'kitchen_assembly') {
      addSuggestions(['electrical', 'elec_lighting_sockets', 'plumbing', 'tiling', 'flooring', 'painting'])
    } else if (catId === 'bathroom' || catId === 'tiling') {
      addSuggestions(['plumbing', 'plumbing_sanitary', 'electrical', 'masonry', 'drywall_attic'])
    } else if (info.group === 'construction') {
      addSuggestions(['electrical', 'plumbing', 'heating', 'drywall_attic', 'flooring', 'painting'])
    } else if (info.group === 'interior_group') {
      addSuggestions(['electrical', 'plumbing', 'painting', 'masonry', 'furniture_assembly'])
    } else if (info.group === 'garden') {
      addSuggestions([
        'garden_mowing',
        'garden_hedges_weeding',
        'wood_chopping',
        'tree_felling_expert',
        'fencing_gates',
        'fencing_paving',
        'excavation_wells',
      ])
    } else if (info.group === 'moving_cleaning_group') {
      addSuggestions([
        'moving_apartments',
        'clearing_cellars',
        'home_cleaning',
        'post_renovation_cleaning',
        'painting',
        'furniture_assembly',
      ])
    } else {
      addSuggestions(['electrical', 'plumbing', 'masonry', 'painting', 'handyman'])
    }
  }

  return suggestionPool.slice(0, 6).map((id) => getCategoryInfo(id))
}

export function isSuitableForCompany(target: ProviderTarget): boolean {
  return target === 'company_only' || target === 'both'
}

export function isSuitableForBrigadnik(target: ProviderTarget): boolean {
  return target === 'brigadnik_only' || target === 'both'
}

export const BRIGADNIK_ALLOWED_CATEGORIES = TRADE_CATEGORIES.filter(
  (c) => c.providerTarget === 'brigadnik_only' || c.providerTarget === 'both'
)

export const COMPANY_ALLOWED_CATEGORIES = TRADE_CATEGORIES.filter(
  (c) => c.providerTarget === 'company_only' || c.providerTarget === 'both'
)

// ─── Uživatel (poptavky_project/users/items/{uid}) ────────────────────────────
export interface AppUser {
  uid: string
  email: string
  displayName: string
  photoURL?: string
  phone?: string
  role: UserRole
  isAdmin?: boolean
  companyId?: string
  brigadnikId?: string
  createdAt: number
  updatedAt: number
}

// ─── Firma / Řemeslník (poptavky_project/companies/items/{companyId}) ─────────
export interface CompanyProfile {
  id: string
  ownerUid: string
  providerType?: 'company' | 'brigadnik'
  ico: string
  dic?: string
  name: string
  legalForm?: string
  isVatPayer?: boolean
  email: string
  phone: string
  website?: string
  contactPerson: string
  address: {
    street: string
    city: string
    zip: string
    region: string
    formatted: string
  }
  locationGps: {
    lat: number
    lng: number
  }
  radiusKm: number
  categories: TradeCategory[]
  description: string
  photos?: string[]
  hourlyRateEstimate?: number // Pro brigádníky např. 250 Kč/hod
  hasOwnTools?: boolean
  hasCar?: boolean
  rating: {
    score: number
    count: number
  }
  credits: number
  blockedCredits: number
  verified: boolean
  verificationStatus: VerificationStatus
  verifiedAt?: number
  rejectionReason?: string
  createdAt: number
  updatedAt: number
}

// ─── Geometrie půdorysu pro čistý 2D náhled ───────────────────────────────────
export interface SimplePoint {
  x: number
  y: number
}

export interface SimpleWall {
  id: string
  start: SimplePoint
  end: SimplePoint
  thickness: number
}

export interface SimpleRoom {
  id: string
  name: string
  points: SimplePoint[]
  color?: string
}

export interface SimpleFurniture {
  id: string
  name?: string
  type?: string
  x: number
  y: number
  width: number
  height: number
  rotation?: number
}

export interface SimpleElectrical {
  id: string
  type: string
  x: number
  y: number
}

export interface PudorysEmbeddedStateSummary {
  name: string
  totalAreaM2: number
  roomCount: number
  wallCount: number
  electricalCount: number
  furnitureCount: number
  roomsSummary: Array<{ name: string; areaM2: number }>
  /** Kompletní JSON projekt z PůdorysOnline Embed API (PudorysProject) */
  pudorysProject: any
}

export interface FloorplanSnapshot {
  projectId: string
  name: string
  externalUrl: string
  totalAreaM2: number
  roomCount: number
  wallCount: number
  electricalCount: number
  furnitureCount: number
  roomsSummary: Array<{ name: string; areaM2: number }>
  /** 1. Aktuální (stávající) stav před úpravou – kompletní PůdorysOnline projekt */
  currentState?: PudorysEmbeddedStateSummary
  /** 2. Nový (požadovaný) stav po úpravě – kompletní PůdorysOnline projekt */
  newState?: PudorysEmbeddedStateSummary
  geometry?: {
    walls: SimpleWall[]
    rooms: SimpleRoom[]
    furniture: SimpleFurniture[]
    electrical: SimpleElectrical[]
  }
}

// ─── Poptávka klienta (poptavky_project/inquiries/items/{inquiryId}) ──────────
export interface Inquiry {
  id: string
  publicCode: string // např. "P-2026-104"
  clientUid: string
  title: string
  category: TradeCategory
  categories?: TradeCategory[] // Podpora více oborů v jedné poptávce (např. elektro + voda + zedník)
  providerTarget: ProviderTarget // 'company_only' | 'brigadnik_only' | 'both'
  selectedSubTasks?: string[] // Konkrétní vybrané podčinnosti daného oboru
  scaleSummary?: string // např. "Střední zahrada (300 – 800 m²)"
  description: string
  location: {
    city: string
    zip: string
    region: string
    lat: number
    lng: number
    streetAndNumber?: string
  }
  urgency: 'flexible' | 'within_month' | 'urgent'
  expectedDate: string
  estimatedBudget: string
  creditCost: number
  floorplan?: FloorplanSnapshot
  photos?: string[]
  status: InquiryStatus
  offersCount: number
  winningOfferId?: string
  awardedCompanyId?: string
  awardedCompanyName?: string
  awardedAt?: number
  reviewToken?: string
  reviewedAt?: number
  reviewId?: string
  reviewRating?: number
  clientContact: {
    name: string
    phone: string
    email: string
  }
  createdAt: number
  updatedAt: number
}

// ─── Cenová nabídka firmy nebo brigádníka (poptavky_project/offers/items/{offerId})
export interface InquiryOffer {
  id: string
  inquiryId: string
  inquiryTitle: string
  inquiryCode: string
  companyId: string
  providerType?: 'company' | 'brigadnik'
  companyName: string
  companyIco: string
  companyCity: string
  companyVerified: boolean
  companyRating: number
  companyRatingCount?: number
  companyPhone: string
  companyEmail: string
  companyContactPerson: string
  priceEstimate: number
  priceNote: string
  earliestStartDate: string
  estimatedDuration: string
  message: string
  blockedCreditsAmount: number
  status: OfferStatus
  createdAt: number
  updatedAt: number
}

// ─── Hodnocení a recenze firmy/brigádníka od klienta (poptavky_project/reviews/items/{reviewId})
export interface CompanyReview {
  id: string
  companyId: string
  companyName: string
  inquiryId: string
  inquiryCode: string
  inquiryTitle: string
  category: TradeCategory
  clientUid: string
  clientName: string
  clientCity: string
  rating: number // 1 až 5 hvězdiček (celkové)
  qualityRating: number // 1 až 5 (kvalita práce)
  communicationRating: number // 1 až 5 (domluva a termín)
  priceRating: number // 1 až 5 (dodržení dohodnuté ceny)
  text: string
  photos?: string[]
  verifiedOrder: boolean
  createdAt: number
}

// ─── Pohyb kreditů (poptavky_project/transactions/items/{txId}) ───────────────
export interface CreditTransaction {
  id: string
  companyId: string
  type: TransactionType
  amount: number
  inquiryId?: string
  offerId?: string
  title: string
  description: string
  balanceAfter: number
  createdAt: number
}

// ─── Automaticky předvyplněná šablona poptávky ────────────────────────────────
export interface AiInquiryDraft {
  prompt: string
  categories: TradeCategory[]
  title: string
  description: string
  subTasks: string[]
  scaleSummary: string
  estimatedBudget: string
  expectedDate?: string
  city?: string
}

